import { defineStore } from 'pinia'
import { getAuth } from 'firebase/auth'
import { useAppStore } from '@/stores/app'
import { useUsersStore } from '@/stores/users'
import { useConversationsStore } from '@/stores/conversations'
import { useFeatureFlagsStore } from '@/stores/featureFlags'
import { useProjectsStore } from '@/stores/projects'
import { useChatScope } from '@/composables/useChatScope'
import { logger } from '@/utils/logger'
import { OPENING_PREFIX } from '@/types/pilotageOpening'
import type { Event, Part, FunctionCall, ActionApprovalResponse, QuestionsPendingResponse, AuthoredDocumentWriteResponse } from '@/models/ChatbotEvent'
import i18n from '@/plugins/i18n'
import { parsePartialJson, jsonToMarkdown } from '@/utils/chatbotParser'
import { getChatbotUrlOverride, getChatbotAppOverride, chatbotDirectTurns } from '@/utils/agentEndpoint'
import { sendAssessmentIntent } from '@/composables/useAssessmentIntents'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import { collection, doc, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { viewRowPath } from '@/composables/useClientViews'
import type { ActivitySpec } from '@/composables/chatActivity'
import {
    cancelCommand,
    conversationCommandsPath,
    conversationErrorI18nKey,
    conversationErrorKey,
    conversationReasonI18nKey,
    decideActionCommand,
    mintChatId,
    mintCommandId,
    sendMessageCommand,
    watchCommand,
    TERMINAL_COMMAND_STATUSES,
    type ConversationCommandState,
    type ConversationCommandStatus,
} from '@/composables/useConversationCommands'
import {
    useChatAttachmentsStore,
    ATTACH_PREFIX,
    attachMarker,
    stripAttachMarker,
    readAttachMarker,
    type ChatAttachmentMarkerFile,
} from '@/stores/chatAttachments'

/** A pending action proposal surfaced as a confirmation card (MYAC-796). */
export interface ActionProposal {
    eventId: string
    proposal: ActionApprovalResponse
}

/** Which register an action targets — the canonical keys the tool returns. */
export type ActionRegister = 'capa' | 'nc' | 'supplier' | 'change_control'

/**
 * A clarifying-question card group surfaced from `ask_user_questions` (MYAC-802).
 * `answered` is inferred, not stored: any user message AFTER the group's event means
 * the conversation moved on — the card collapses to read-only. Survives reload for
 * free (groups are re-derived from the session events on every render).
 */
export interface QuestionGroup {
    eventId: string
    /** Index of the tool-response event in `events` — used for timeline anchoring. */
    eventIndex: number
    group: QuestionsPendingResponse
    answered: boolean
}

/**
 * A persistent result card (MYAC-796). After the user approves a refresh, one of these is
 * pushed and stays in the conversation. It binds (in the component) to the register's store
 * — the same source the register page uses — so it shows "refreshing…" then the result, and
 * never disappears. The user can keep chatting underneath it.
 */
export interface ActionResultCard {
    id: string
    register: ActionRegister
    // Client timestamp (ms) when the refresh was approved. The card treats an assessment as
    // THIS run only once its updated_at is at/after this — so a stale 'done' from a previous
    // run shows "refreshing" (not the old result), and a run that finished before the card
    // mounted still resolves to 'done' instead of spinning forever.
    startedAt: number
    // Id of the message the card is anchored AFTER, so it stays fixed in the conversation
    // history (instead of floating at the bottom as new messages arrive).
    afterMessageId: string | null
}

/**
 * The chat tool that writes documents (MYAC-1161). One tool creates, edits, appends,
 * renames and deletes — so the name identifies the family, and `action` picks the
 * creation out of it.
 */
export const WRITE_AUTHORED_DOCUMENT_TOOL = 'write_authored_document'

/**
 * A document the assistant opened during this conversation (MYAC-1162).
 *
 * One card per document, on CREATION only. Edits produce no card at all — the
 * feedback for those is the live panel and the bot's own text, and a card per edit
 * would bury a long report under its own changelog.
 *
 * Derived from the events on every render, like `questionGroups` and unlike
 * `resultCards`, so it survives a reload for free.
 */
export interface AuthoredDocumentCard {
    eventId: string
    /** Index of the tool-response event in `events` — used for timeline anchoring. */
    eventIndex: number
    docId: string
    title: string
    revision: number
}

const ACTION_DECISION_PREFIX = '__ACTION_DECISION__'
/** MYAC-883 — the decision payload for the post-run delta card. */
export const APPLY_PROPOSAL_ACTION = 'apply_register_proposal'
// MYAC-796 step 2 — the auto-continued follow-up is sent with this prefix so it's hidden
// from the transcript (only the agent's answer shows); the chatbot strips the prefix.
export const ACTION_FOLLOWUP_PREFIX = '__ACTION_FOLLOWUP__'

/**
 * The prefixes that mean "this message is the app talking, not the user".
 * Anything sent with one of them must reach the agent byte-identical: both the
 * chatbot's decision handler and this store's own transcript filter dispatch on
 * `startsWith`, so a single character in front of one breaks both.
 *
 * A new sentinel MUST be added here — that is the whole contract.
 */
const MACHINE_SENTINELS = [ACTION_DECISION_PREFIX, ACTION_FOLLOWUP_PREFIX] as const

export function isMachineSentinel(text: string): boolean {
    return MACHINE_SENTINELS.some(prefix => text.startsWith(prefix))
}
/**
 * MYAC-919 — a machine-readable reference to the object the user is asking
 * about, carried in FRONT of their own message:
 *
 *     __FOCUS__{"kind":"requirement","uid":"…","code":"7.5.1"}\n<their message>
 *
 * The message text is the only channel the browser has for this: the agent server
 * refuses a client-supplied `state_delta` outright (403, by design), so screen
 * context cannot ride session state. The chat agent's `bmc_apply_focus_reference`
 * strips the marker and tells the model which object the turn is about before it
 * sees anything.
 *
 * It is attached to messages the user CHOSE to send — opening the panel on an
 * object attaches nothing and costs nothing. Only the marker line is hidden from
 * the transcript (see doneMessages); the message itself is theirs and stays.
 *
 * Doctrine: this is UX context, never an authorization input — the tools resolve
 * the project from the SESSION identity, so a reference can only ever name
 * something inside the caller's own project.
 */
export const FOCUS_PREFIX = '__FOCUS__'

/**
 * What the user is looking at. `kind` is the agent's contract — widen it there
 * first (`_FOCUS_KINDS` in the chat agent's callbacks).
 *
 * One field carries the weight: `uid` is the REQUIREMENT that resolves the
 * object, because that is what the agent can actually look up
 * (`get_referential_requirement`, which returns the verdict, its evidence AND
 * its remediations). For a requirement that is itself; for an action it is the
 * requirement it is anchored on. A document has no single one, so it names the
 * requirements that expect it instead.
 */
export interface ChatFocusRef {
    kind: 'requirement' | 'document' | 'remediation' | 'finding' | 'obligation' | 'register_entry' | 'task' | 'authored_document'
    /**
     * The uid that resolves this object.
     *
     * For the referential kinds this is the REQUIREMENT uid, because that is what
     * `get_referential_requirement` takes. Two kinds are the exception, both
     * because a requirement is not what resolves them:
     *
     * - `task` — a board task is resolved by its own document id through
     *   `get_kanban_task`, and it may have no requirement behind it at all: a
     *   hand-created task has no proposal, let alone a requirement (MYAC-1127).
     * - `authored_document` — a document the assistant wrote, resolved by its own
     *   `doc_id` through `read_authored_document`. Like a task, it has no
     *   requirement behind it: the chat wrote it, nothing in the referential
     *   expects it. NOT the `document` kind above, which is a Complétude GAP
     *   document named by the requirements that expect it (MYAC-1162).
     * - `obligation` — the schedule row's `row_key`, because the schedule is what
     *   resolves it and a row is not a requirement. OPTIONAL here, unlike a
     *   task's: an obligation is identified by `name` and the key only
     *   disambiguates the namesakes lot B created (MYAC-1171), so a row
     *   projected before lot B still attaches without one.
     */
    uid?: string
    /** Its printed code, as the screen shows it. */
    code?: string
    /**
     * What it is called. For a register entry this is the CANONICAL register key
     * (`ActionRegister` — the four registers by the key their tools take)
     * — the same value the agent's `get_qms_register(kind)` takes, so nothing has
     * to translate «Registre CAPA» on the way; `code` then carries the record's
     * own reference and `label` what the user reads.
     */
    name?: string
    /** document: the requirements that expect it. finding: the ones it touches. */
    uids?: string[]
    /**
     * The kind's own sub-state — a generic slot, filled differently per kind.
     *
     * document: which gap list it came from. task: which board column it sits in
     * (MYAC-1127), so "why is this still in To do?" does not cost the agent a
     * read to answer. The agent's key whitelist takes it as an opaque string and
     * each kind's directive says what its value means, so widening the union
     * here is the whole of adding a new one.
     */
    state?: 'to_attach' | 'cited' | 'missing' | 'todo' | 'in_progress' | 'done'
    /**
     * How the subject is named on screen. It DOES travel now (the agent's key
     * whitelist drops it): the header indicator labels every past turn by
     * re-reading the marker off its own event, so a conversation that outlives
     * the click — a reload, later history — can still say what each answer was
     * about. ~30 characters against a 27k-token turn.
     */
    label?: string
    /**
     * The referential the subject belongs to — its adoption id (`qms:iso_13485`)
     * — for the requirement-anchored kinds (MYAC-1477 follow-up, the prod mix-up
     * of 2026-10-13). A printed code repeats across adopted standards (ISO 13485
     * and IEC 62304 both have a 5.1), and the uid alone did not stop the agent
     * from answering by code; carried, the agent scopes every read to it.
     */
    referential?: string
}

/**
 * Identity of an attachment, for "is this the same one?" — the button that
 * attached it detaches it on a second press, and `uid` alone cannot tell two
 * documents apart (they have none).
 */
export function focusKey(focus: ChatFocusRef): string {
    // `code` is part of the identity: two rows of the SAME register differ by
    // nothing else, and pressing one bubble must not toggle another off.
    return [focus.kind, focus.uid ?? '', focus.name ?? '', focus.code ?? ''].join('|')
}

/** The wire form. The agent whitelists the keys it understands and drops `label`. */
export function focusMarker(focus: ChatFocusRef): string {
    const { kind, uid, code, name, uids, state, label, referential } = focus
    const payload = { kind, uid, code, name, uids, state, label, referential }
    // JSON.stringify drops undefined values on its own — the marker stays as
    // short as the object actually is.
    return `${FOCUS_PREFIX}${JSON.stringify(payload)}`
}

/** The message without its marker line — what the user actually wrote. */
export function stripFocusMarker(text: string): string {
    if (!text.startsWith(FOCUS_PREFIX)) return text
    const newline = text.indexOf('\n')
    return newline === -1 ? '' : text.slice(newline + 1).replace(/^\s+/, '')
}

/** MYAC-1313 — the message without its opening-marker line. Mirrors
 * `stripFocusMarker` exactly; the marker is plumbing, never what the user wrote. */
export function stripOpeningMarker(text: string): string {
    if (!text.startsWith(OPENING_PREFIX)) return text
    const newline = text.indexOf('\n')
    return newline === -1 ? '' : text.slice(newline + 1).replace(/^\s+/, '')
}

/**
 * MYAC-1522 (B3) — what MAC « just said » to a conversation, read back off its
 * first turn's opening marker, like `readFocusMarker`: the raw event is what the
 * agent was told. Null when the text carries none or one we cannot read.
 */
export function readOpeningMarker(text: string | undefined): string | null {
    if (!text?.startsWith(OPENING_PREFIX)) return null
    const newline = text.indexOf('\n')
    const raw = newline === -1 ? text.slice(OPENING_PREFIX.length) : text.slice(OPENING_PREFIX.length, newline)
    try {
        const parsed = JSON.parse(raw) as { message?: unknown }
        return typeof parsed?.message === 'string' && parsed.message.trim() ? parsed.message : null
    } catch {
        return null
    }
}

/**
 * The subject a sent message carried, read back off its own text.
 *
 * Attribution costs nothing to store because the marker never left: the raw
 * event is what the server received. That is what lets the header say which
 * subject the answer you are LOOKING at came from, including turns from before
 * a reload — no side table to keep in step with the transcript.
 */
export function readFocusMarker(text: string | undefined): ChatFocusRef | null {
    if (!text?.startsWith(FOCUS_PREFIX)) return null
    const newline = text.indexOf('\n')
    const raw = newline === -1 ? text.slice(FOCUS_PREFIX.length) : text.slice(FOCUS_PREFIX.length, newline)
    try {
        const parsed = JSON.parse(raw) as Partial<ChatFocusRef>
        return parsed?.kind ? (parsed as ChatFocusRef) : null
    } catch {
        // A marker we cannot read is a marker we do not label — never a crash in
        // the middle of rendering a conversation.
        return null
    }
}
const ACTION_REGISTERS: ActionRegister[] = ['capa', 'nc', 'supplier', 'change_control']

/**
 * Process text parts: if response format is json_markdown, parse JSON and convert to markdown
 * Otherwise, return parts as-is
 */
function processMessageParts(parts: Part[], responseFormat: 'markdown' | 'json_markdown'): Part[] {
    if (responseFormat !== 'json_markdown') {
        // Markdown mode: return parts as-is
        return parts
    }

    // json_markdown mode: accumulate text across all parts and try to parse as JSON
    const textParts = parts.filter(part => part.text !== undefined && part.text !== null && part.text.trim() !== '')

    if (textParts.length === 0) {
        return parts
    }

    // Accumulate all text from parts
    const accumulatedText = textParts.map(part => part.text).join('')

    // Try to parse as JSON
    const parsedJson = parsePartialJson(accumulatedText)

    if (parsedJson) {
        // Successfully parsed JSON, convert to markdown
        try {
            const markdown = jsonToMarkdown(parsedJson)
            // Replace all text parts with a single part containing the markdown
            const nonTextParts = parts.filter(part => part.text === undefined || part.text === null)
            return [
                ...nonTextParts,
                { ...textParts[0], text: markdown },
                ...textParts.slice(1).map(part => ({ ...part, text: '' }))
            ].filter(part => part.text !== '') // Remove empty text parts
        } catch {
            // If markdown conversion fails, keep original parts
            return parts
        }
    }

    // Not valid JSON, return parts as-is
    return parts
}

/**
 * MYAC-882 — a part only earns a bubble if it has VISIBLE text. Intermediate agent
 * steps (tool calls, thinking) often carry empty or whitespace-only text parts;
 * rendering them produced empty padded bubbles that stacked up as blank lines.
 */
function hasRenderableText(part: Part): boolean {
    return part.text !== undefined && part.thought !== true && part.text.trim() !== ''
}

/**
 * MYAC-802 — an event that carries a functionCall is usually an intermediate step:
 * its text parts are process narration at best, and Gemini sometimes OVERFLOWS its
 * thought summary into an unflagged text part right before the call ("Let's call the
 * tool. For 'capa', I'll query…"), which `thought !== true` filtering can't catch.
 *
 * MYAC-1152 — "usually" is the whole subtlety: when the model ends its turn with a
 * question card, its answer travels in the SAME event as the card's call. See
 * `rendersTurnAnswer` for the two witnesses that tell the two apart.
 */
function isToolCallEvent(event: Event): boolean {
    return event.content?.parts.some(part => part.functionCall !== undefined) ?? false
}

/**
 * A message the USER actually sent, as opposed to a tool result — which ADK also
 * stamps `content.role === 'user'` (verified on every function-response event of a
 * live session). Anything inferring "the conversation moved on" must use this, not
 * the raw role. MYAC-1152.
 */
function isRealUserMessage(event: Event): boolean {
    if (event.content?.role !== 'user') return false
    // MYAC-1519 — this tab's optimistic copy of a message the conversation has not
    // taken in (sending, refused, not delivered) is not a message of the
    // conversation yet: a refused one must not answer the question cards above it.
    if (event.id?.startsWith(PENDING_EVENT_PREFIX)) return false
    return !event.content.parts.some(part => part.functionResponse !== undefined)
}

function hasAnyRenderableText(event: Event): boolean {
    return event.content?.parts.some(hasRenderableText) ?? false
}

/**
 * The statuses a tool returns when it hands control back to the user — each one is
 * drawn as a card. A call that got one of these ENDED the turn by construction, which
 * is what makes its own text the answer rather than narration (MYAC-1152).
 */
const PAUSE_STATUSES = new Set(['questions_pending', 'approval_required'])

/**
 * MYAC-1382 — did the turn that started at `base` leave the user anything to see: a
 * model text, or a card asking them to act (a card-only turn ends on its paused tool
 * response, with no text after it)? A stream the platform cuts — the request timeout,
 * a dropped connection, an instance going away — ends as a CLEAN close: no error
 * frame, so no error path runs, and before this check the turn left nothing at all.
 */
export const TURN_ENDED_WITHOUT_ANSWER = 'The response stream ended before an answer arrived'

export function turnLeftSomethingToShow(events: Event[], base: number): boolean {
    return events.slice(base).some((event) => {
        if (event.partial) return false
        // The card check comes FIRST: ADK sends every tool result as a `role: 'user'`
        // event, so the role filter below would hide the card that ended the turn.
        const raisedCard = (event.content?.parts ?? []).some((part) => {
            const status = (part.functionResponse?.response as { status?: string } | undefined)?.status
            return status !== undefined && PAUSE_STATUSES.has(status)
        })
        if (raisedCard) return true
        return event.content?.role !== 'user' && hasAnyRenderableText(event)
    })
}

/**
 * Which turn each event belongs to. ADK stamps `invocationId` on every event; when it
 * is missing (an older event, another agent) a turn falls back to "everything since
 * the last real user message".
 */
function turnKeys(events: Event[]): string[] {
    let fallback = 0
    return events.map(event => {
        if (isRealUserMessage(event)) fallback += 1
        const id = event.invocationId
        return typeof id === 'string' && id !== '' ? id : `#${fallback}`
    })
}

/**
 * Indices of the events whose call got a result that asked the user to act.
 *
 * Matched by call id, which ADK populates on both sides (67/67 on a live session).
 * A call that carries NO id falls back to the tool name — but positionally: the n-th
 * id-less call of a tool binds to the n-th id-less response of that tool, in order. A
 * flat name match would let one card's status vouch for a sibling call of the same
 * tool: a retry in the same turn would render its failed attempt's narration as if it
 * had ended the turn.
 *
 * The two lanes never mix. A call WITH an id is decided by that id alone, even when no
 * response carries it (an aborted turn, or one still streaming) — it must not reach
 * into the id-less queue and consume a result that belongs to another call. Ids come
 * from one serializer, so they are present on both sides or on neither.
 */
function pauseCallEventIndices(events: Event[]): Set<number> {
    const pauseById = new Map<string, boolean>()
    const pauseByName = new Map<string, boolean[]>()
    for (const event of events) {
        for (const part of event.content?.parts ?? []) {
            const response = part.functionResponse
            if (!response) continue
            const status = (response.response as { status?: string } | undefined)?.status
            const isPause = status !== undefined && PAUSE_STATUSES.has(status)
            if (response.id) pauseById.set(response.id, isPause)
            else if (response.name) {
                const queue = pauseByName.get(response.name) ?? []
                queue.push(isPause)
                pauseByName.set(response.name, queue)
            }
        }
    }
    const consumed = new Map<string, number>()
    const out = new Set<number>()
    events.forEach((event, index) => {
        for (const part of event.content?.parts ?? []) {
            const call = part.functionCall
            if (!call) continue
            if (call.id) {
                if (pauseById.get(call.id)) out.add(index)
                continue
            }
            const queue = pauseByName.get(call.name)
            if (!queue) continue
            const at = consumed.get(call.name) ?? 0
            if (at >= queue.length) continue
            consumed.set(call.name, at + 1)
            if (queue[at]) out.add(index)
        }
    })
    return out
}

/**
 * MYAC-1152 — does this tool-call event carry the model's answer, or mid-turn
 * narration? Two independent witnesses that the model stopped there; either is enough.
 *
 * W1 — position: the turn holds no later renderable text, so this was the last thing
 * the model said. Verified against a live 167-event session: it picks the right event
 * in all 11 turns and suppresses all 23 leaked-narration events.
 *
 * W2 — pause: one of this event's calls got a response that hands control to the user
 * (a card is drawn from it), so the turn ended whether or not the model kept talking.
 * This keys on the very status contract the cards already need, so it cannot drift out
 * of sync with them.
 */
function rendersTurnAnswer(
    index: number,
    turnKey: string,
    lastTextOfTurn: Map<string, number>,
    pauseCallEvents: Set<number>,
): boolean {
    return lastTextOfTurn.get(turnKey) === index || pauseCallEvents.has(index)
}

export interface ChatbotMessage {
    id: string
    /**
     * The subject this turn is about (MYAC-919 follow-up): read off the user
     * message's own marker, and inherited by the model's answer to it — an
     * answer belongs to the question that asked it.
     */
    focus?: ChatFocusRef | null
    /**
     * Files this turn attached (MYAC-1182), read off its own `__ATTACH__` marker
     * the same way `focus` is read off `__FOCUS__` — set only on the user message
     * that carried them, never inherited by later turns (unlike `focus`: a
     * summary chip belongs to the message that has the files, not to the
     * conversation's ongoing subject).
     */
    attachments?: ChatAttachmentMarkerFile[] | null
    parts: Part[]
    role: 'user' | 'model'
    isUserMessage: boolean
    isStreaming: boolean
    isPartial: boolean
    timestamp: number
    raw?: Event
    /**
     * MYAC-1519 — a project-chat message this tab sent that the conversation has
     * not taken in yet (its command is still `requested`/`accepted`). Shown at
     * once, marked as sending.
     */
    pending?: boolean
    /** MYAC-1519 — the i18n key of why the message was refused; it can be resent. */
    rejectedReasonKey?: string | null
    /** MYAC-1269 — who wrote this user message, when the backend recorded it. */
    authorUid?: string | null
    /** MYAC-1519 — the command that carried this message (pending / refused ones only). */
    commandId?: string
}

// ─────────────────────────────────────────────────────────────────────────────
// MYAC-1519 (option B) — a project chat is WRITTEN as command docs and READ from
// the `views/chat_sessions` rows. The types below are the projection's shape.
// ─────────────────────────────────────────────────────────────────────────────

/** One card of a `card` row. `payload` carries the fields the cards read today. */
export interface ChatCardRow {
    kind: 'approval' | 'questions' | 'authored_document'
    /** The tool call's `function_response.id`. */
    call_id?: string
    payload?: Record<string, unknown>
}

/** A row of `views/chat_sessions/rows/{chat_id}/messages`, ordered by `seq`. */
export interface ChatSessionMessageRow {
    id: string
    chat_id?: string
    seq: number
    kind: 'message' | 'activity' | 'card'
    role: 'user' | 'model'
    text?: string
    label_key?: string
    label_args?: Record<string, string>
    label?: string
    card?: ChatCardRow
    /** Only when one event carried 2+ tool results; `card` is then the first. */
    cards?: ChatCardRow[]
    /** MYAC-1269 — who wrote this user message (absent: the owner, before sharing). */
    author_uid?: string
}

/** The conversation's own row (`views/chat_sessions/rows/{chat_id}`). */
export interface ChatSessionRunRow {
    run_status?: 'idle' | 'running'
    run_started_at?: unknown
    run_ended_at?: unknown
    run_command_id?: string | null
    title?: string
    message_count?: number
    updated_at?: unknown
    owner_uid?: string
    /** MYAC-918 — the list fields: a rename always wins; a done turn's end; a proposal awaiting the person. */
    title_edited?: boolean
    last_activity_at?: unknown
    pending_approval?: boolean
    deleted?: boolean
    /** MYAC-1269 — sharing, projected from the chat doc's truth in the same write. */
    last_activity_by?: string
    visibility?: 'private' | 'participants' | 'project'
    project_right?: 'read' | 'write'
    /** Every named participant: who may READ besides the owner (and the project, when `visibility` is `project`). */
    participant_uids?: string[]
    /** The named participants who may WRITE. */
    writer_uids?: string[]
}

/** MYAC-1269 — what a person may do in a conversation: null when they may not even read it. */
export type ConversationRight = 'owner' | 'write' | 'read' | null

/**
 * The right of `uid` on a conversation row — the front's copy of the backend's
 * `ChatSharing.effective_right` (core_models_services), the one place the
 * « stronger right wins » rule is spelled: a named writer keeps write on a
 * project-wide read-only share; a project-wide edit share lifts a named reader.
 * A row with no sharing fields is private (before MYAC-1269, or stopped).
 */
export function conversationRightOf(row: ChatSessionRunRow | null | undefined, uid: string): ConversationRight {
    if (!row || !uid) return null
    if (row.owner_uid && row.owner_uid === uid) return 'owner'
    const named = row.participant_uids?.includes(uid) ? (row.writer_uids?.includes(uid) ? 'write' : 'read') : null
    const project = row.visibility === 'project' ? (row.project_right === 'write' ? 'write' : 'read') : null
    if (named === 'write' || project === 'write') return 'write'
    if (named === 'read' || project === 'read') return 'read'
    return null
}

/** `…/rows/{chat}/decisions/{proposal_id}` — who decided a proposal (MYAC-1269). */
export interface ChatDecisionRow {
    proposal_id?: string
    decision?: string
    decided_by?: string
    decided_at?: unknown
    command_id?: string
}

/** A decision row, typed: what was decided on a proposal, by whom, when. */
export interface ChatDecision {
    proposalId: string
    decision: 'approve' | 'decline' | 'unknown'
    decidedBy: string
    decidedAt: number | null
}

/** A proposal someone ELSE settled (MYAC-1269): the card stays, with their name on it. */
export interface SettledProposal extends ActionProposal {
    /** Index of the proposal's event in `events` — timeline anchoring, like a document card. */
    eventIndex: number
    decision: ChatDecision
}

/** The decision row as the backend writes it, tolerant: an unknown verdict is kept, unnamed. */
export function decisionFromRow(id: string, row: ChatDecisionRow): ChatDecision {
    const verdict = row.decision === 'approve' || row.decision === 'accepted' ? 'approve'
        : row.decision === 'decline' || row.decision === 'declined' || row.decision === 'rejected' ? 'decline'
            : 'unknown'
    return {
        proposalId: row.proposal_id || id,
        decision: verdict,
        decidedBy: typeof row.decided_by === 'string' ? row.decided_by : '',
        decidedAt: timeMillis(row.decided_at),
    }
}

/** `…/live/current` — the draft of the answer being written; deleted when the turn ends. */
export interface ChatLiveDraft {
    turn_id?: string
    text?: string
    label_key?: string
    label_args?: Record<string, string>
    seq?: number
    updated_at?: unknown
}

/** A command this tab sent and still follows. */
interface PendingCommand {
    commandId: string
    kind: 'send' | 'decide'
    /** send: the wire message. */
    text: string
    /** decide: the proposal it answers. */
    proposalId?: string
    /** `stalled`: still `requested` after the admission window (local state only). */
    status: ConversationCommandStatus | 'stalled'
    reasonKey?: string
    errorKey?: string
    /**
     * The conversation's rows from this seq on belong to the turn it started; its
     * optimistic copy is drawn here (before them), and only a user row from this seq
     * on can be its echo.
     */
    baseSeq: number
    /** Its user row has arrived: the optimistic copy is no longer drawn. */
    echoed: boolean
    /**
     * send: replaced by a resend while still undelivered. Never drawn; still
     * followed so a late admission is told apart from the resend (its user row
     * echoes it, not the resend), and dropped silently when it ends.
     */
    superseded?: boolean
    at: number
}

/**
 * A row saying `running` whose turn started longer ago than this is a turn that
 * died with its instance: the server's own stale window (900 s).
 */
export const TURN_STALE_MS = 900_000
/** A command still `requested` after this is shown as not delivered (resend offered). */
export const COMMAND_ADMISSION_MS = 60_000
/**
 * A decision waits this long for its command to finish before `respondTo*` returns;
 * the follow-ups still run if it finishes later, never before.
 */
const DECISION_SETTLE_MS = 120_000
/**
 * A live draft is the server writing right now. Once the row says the turn is over
 * (or is stale), a draft whose own stamp is older than this is a leftover (its
 * end-of-turn delete failed) and no longer holds the chat. Missing stamp = stale.
 */
export const LIVE_DRAFT_STALE_MS = 120_000
/** `this.error` value for a turn whose command failed; the suffix is the error key. */
export const COMMAND_FAILED_PREFIX = 'command_failed:'
/** The live draft arrives in ~400 ms packets; it is typed out over about that long. */
const TYPEWRITER_TICK_MS = 30
const TYPEWRITER_TICKS = 12

const ACTIVE_COMMAND_STATUSES: ReadonlySet<string> = new Set(['requested', 'queued', 'accepted', 'running'])
const PENDING_EVENT_PREFIX = 'pending-'
const ROW_EVENT_PREFIX = 'row-'

/** Milliseconds of a Firestore Timestamp, a Date, or a number; null otherwise. */
export function timeMillis(value: unknown): number | null {
    if (value === null || value === undefined) return null
    if (typeof value === 'number') return value
    if (value instanceof Date) return value.getTime()
    const maybe = value as { toMillis?: () => number }
    return typeof maybe.toMillis === 'function' ? maybe.toMillis() : null
}

/**
 * Is a turn of this conversation running? `running` on the row, bounded: a turn
 * started more than `TURN_STALE_MS` ago is interrupted, not running. A row
 * whose start time has not landed yet (server timestamp pending) counts as running.
 */
export function turnRowIsRunning(row: ChatSessionRunRow | null | undefined, now = Date.now()): boolean {
    if (row?.run_status !== 'running') return false
    const started = timeMillis(row.run_started_at)
    return started === null || now - started < TURN_STALE_MS
}

/**
 * Does the live draft say a turn is being written? Only while the row says it is
 * running, or — the row not saying `idle` — while the draft itself is fresh (its
 * `updated_at` within `LIVE_DRAFT_STALE_MS`). A `live/current` left behind by a
 * turn whose end-of-turn delete failed would otherwise hold the chat forever.
 */
export function liveDraftHoldsTurn(
    draft: ChatLiveDraft | null | undefined,
    row: ChatSessionRunRow | null | undefined,
    now = Date.now(),
): boolean {
    if (!draft) return false
    if (turnRowIsRunning(row, now)) return true
    if (row?.run_status === 'idle') return false
    const updated = timeMillis(draft.updated_at)
    return updated !== null && now - updated < LIVE_DRAFT_STALE_MS
}

const CARD_TOOL_NAME: Record<ChatCardRow['kind'], string> = {
    approval: 'approval_required',
    questions: 'ask_user_questions',
    authored_document: WRITE_AUTHORED_DOCUMENT_TOOL,
}

/**
 * A card row as the tool result the card getters already read
 * (`approvalProposals`, `questionGroups`, `authoredDocumentCards`). The
 * projection drops `status` (the card kind says it) and carries the
 * authored document's `result` flat, so both are put back here.
 */
export function cardFunctionResponse(card: ChatCardRow): { id?: string; name: string; response: Record<string, unknown> } {
    const payload = card.payload ?? {}
    let response: Record<string, unknown>
    if (card.kind === 'approval') {
        response = { ...payload, status: 'approval_required' }
    } else if (card.kind === 'questions') {
        response = { ...payload, status: 'questions_pending' }
    } else {
        // Only a successful creation is projected as a card.
        response = { status: 'success', result: { action: 'created', ...payload } }
    }
    return { id: card.call_id, name: CARD_TOOL_NAME[card.kind] ?? card.kind, response }
}

/**
 * The conversation rows as the events the transcript getters already read. A
 * message row is one event; a card row is one tool-result event (ADK stamps
 * those `role: 'user'`, which the getters expect); activity rows are dropped —
 * the steps of a turn show in the working indicator, never as bubbles.
 */
/**
 * Where a running turn this tab did not start begins: at the last question,
 * unless an answer already follows it, in which case the running turn's own
 * question is not projected yet and the turn begins after every row seen.
 */
export function foundTurnBaseSeq(rows: ChatSessionMessageRow[]): number {
    const messages = rows.filter(r => r.kind === 'message')
    const last = messages[messages.length - 1]
    if (!last) return 0
    if (last.role === 'model') return Math.max(...rows.map(r => r.seq)) + 1
    return last.seq
}

export function rowsToEvents(rows: ChatSessionMessageRow[]): Event[] {
    const out: Event[] = []
    for (const row of rows) {
        const id = `${ROW_EVENT_PREFIX}${row.id}`
        if (row.kind === 'message') {
            out.push({
                id, content: { role: row.role, parts: [{ text: row.text ?? '' }] },
                ...(row.author_uid ? { authorUid: row.author_uid } : {}),
            } as Event)
        } else if (row.kind === 'card') {
            const cards = row.cards?.length ? row.cards : (row.card ? [row.card] : [])
            if (!cards.length) continue
            out.push({
                id,
                content: { role: 'user', parts: cards.map(c => ({ functionResponse: cardFunctionResponse(c) })) },
            } as Event)
        }
    }
    return out
}

/**
 * The per-project memory of the conversation on screen — per viewer, per BROWSER
 * (localStorage): closing the tab and opening a new one finds the conversation
 * again, which is the whole point of a turn that outlives the tab. Another device
 * or browser does not (that is the conversation list, MYAC-918). A convenience
 * only: the chat id, nothing else, and every access guarded.
 */
function rememberedChatKey(uid: string, projectId: string, appName: string | null): string {
    // per USER as well (review of PR 921): on a shared browser the next person
    // signing in must not resume the previous person's conversation — the same
    // shape as `tenantStorageKey(userId)` in `stores/tenants.ts`
    return `mac.chat.current.${uid}.${projectId}.${appName ?? ''}`
}
function currentUid(): string {
    // the state, never the `uid` getter (it throws with nobody signed in, and a
    // getter depending on it would re-run it outside this try/catch)
    try { return useUsersStore().user?.uid ?? '' } catch { return '' }
}
/** A Firestore listener refusal: the rules no longer admit this person. */
export function isPermissionDenied(err: unknown): boolean {
    return typeof err === 'object' && err !== null && (err as { code?: unknown }).code === 'permission-denied'
}
/**
 * MYAC-1269 (Aymeric, 27/10) — only while the conversations list is off (the
 * `enable_conversations` flag): with the list, a reload starts a fresh
 * conversation and the list is the way back (an old one reopened by itself
 * caught chips sent from the dashboard and grew conversations without end).
 * Without it, this memory is the only way back after a reload, so it stays.
 */
function remembersLastChat(): boolean {
    try { return !useFeatureFlagsStore().isConversationsEnabled } catch { return true }
}
function readRememberedChat(projectId: string, appName: string | null): string | null {
    const uid = currentUid()
    if (!uid || !remembersLastChat()) return null
    try { return localStorage.getItem(rememberedChatKey(uid, projectId, appName)) } catch { return null }
}
function rememberChat(projectId: string, appName: string | null, chatId: string): void {
    const uid = currentUid()
    if (!uid || !remembersLastChat()) return
    try { localStorage.setItem(rememberedChatKey(uid, projectId, appName), chatId) } catch { /* convenience only */ }
}
function forgetChat(projectId: string, appName: string | null): void {
    const uid = currentUid()
    if (!uid) return
    try { localStorage.removeItem(rememberedChatKey(uid, projectId, appName)) } catch { /* convenience only */ }
}

// Timers of the project path. Module-level: they hold no data, only wake the store.
let typewriterTimer: ReturnType<typeof setInterval> | null = null
let runStaleTimer: ReturnType<typeof setTimeout> | null = null
const admissionTimers = new Map<string, ReturnType<typeof setTimeout>>()
// One waiter per command; `null` timeout waits for the command's end, however long.
const commandWaiters = new Map<string, (status: string) => void>()

function stopTypewriter(): void {
    if (typewriterTimer) clearInterval(typewriterTimer)
    typewriterTimer = null
}

/**
 * Type the live draft out towards its latest text, so 400 ms packets read as
 * writing. Called on every packet: the step is set once per packet, so the new
 * text is fully shown after `TYPEWRITER_TICKS` ticks — about one packet's time.
 */
let typewriterStep = 1
function startTypewriter(store: { draftTarget: string; shownDraftText: string }): void {
    const behind = store.draftTarget.length - store.shownDraftText.length
    typewriterStep = Math.max(1, Math.ceil(behind / TYPEWRITER_TICKS))
    if (typewriterTimer) return
    typewriterTimer = setInterval(() => {
        const target = store.draftTarget
        const shown = store.shownDraftText
        if (!target.startsWith(shown)) {
            store.shownDraftText = target
        } else if (shown.length < target.length) {
            store.shownDraftText = target.slice(0, shown.length + typewriterStep)
        }
        if (store.shownDraftText.length >= target.length) stopTypewriter()
    }, TYPEWRITER_TICK_MS)
}

function clearProjectTimers(): void {
    stopTypewriter()
    if (runStaleTimer) clearTimeout(runStaleTimer)
    runStaleTimer = null
    for (const timer of admissionTimers.values()) clearTimeout(timer)
    admissionTimers.clear()
    for (const resolve of commandWaiters.values()) resolve('cancelled')
    commandWaiters.clear()
}

export const useChatbotStore = defineStore('chatbot', {
    state: () => ({
        sessionId: null as string | null,
        projectId: null as string | null,
        events: [] as Event[],
        // Index into `events` marking where the current streaming turn began — so the
        // activity indicator only considers THIS turn's tool calls, not stale ones.
        streamingBaseEventIndex: 0,
        isCreatingSession: false,
        isLoading: false,
        isStreaming: false,
        baseUrl: null as string | null,
        error: null as string | null,
        activeReader: null as ReadableStreamDefaultReader<Uint8Array> | null,
        streamCancelled: false,
        // Per-context agent app override (MYAC-750). When set, the chat targets this
        // ADK app_name instead of the global `chatbot_name` from config — lets a
        // context-scoped chat (e.g. the CAPA documentary chat) use its own agent.
        // Dev-only: seedable via VITE_CHATBOT_APP_OVERRIDE to chat with a draft
        // agent through the front (dead code in deployed builds).
        appNameOverride: getChatbotAppOverride() as string | null,
        // Unsent input draft — kept in the store so it survives the chat drawer being
        // closed/reopened (the drawer destroys its content). Cleared on send / reset. MYAC-750.
        draftMessage: '',
        // MYAC-919 — the object the user opened the chat ON (a requirement they
        // clicked «Discuss» on). Shown as a removable chip above the input until
        // the next message carries it away. Never sent on its own: attaching
        // context to nothing would be a paid turn the user never asked for.
        pendingFocus: null as ChatFocusRef | null,
        // MYAC-1522 — a message another surface asked the chat to send as soon as
        // it is up (chatPanel.openAndSend). ChatInterface consumes it through its
        // own send path, so the credit gate and the opening marker still apply.
        pendingSend: null as string | null,
        // MYAC-1522 (B3) — what MAC « just said » on the surface that handed the
        // message over, when the chat it lands in shows something else: the welcome
        // letter written for the person, whose reply opens a project whose own
        // opening is its Pilotage summary. Set by chatPanel.openAndSend only, with
        // the message; rides that turn in place of the chat's own opening, then
        // cleared.
        pendingOpening: null as { message: string } | null,
        // MYAC-796 — action approval + result cards.
        // Proposal ids the user already approved/declined → hide their approval cards.
        resolvedProposalIds: [] as string[],
        // Persistent result cards: one per approved refresh, stays in the conversation.
        // The card component binds to the register's store for live status + result.
        resultCards: [] as ActionResultCard[],
        // ── MYAC-1519 — the project path (commands + views) ──
        // True while the conversation on screen is a project chat followed in the
        // views; `sessionId` is then its chat id. The no-project chat keeps `false`.
        projectMode: false,
        chatRow: null as ChatSessionRunRow | null,
        messageRows: [] as ChatSessionMessageRow[],
        liveDraft: null as ChatLiveDraft | null,
        // The latest draft text, kept after the draft doc is deleted until the run
        // ends (the final message is held back until then, so nothing flickers).
        draftTarget: '',
        // What the typewriter has shown of `draftTarget` so far.
        shownDraftText: '',
        pendingCommands: [] as PendingCommand[],
        // Approvals decided in ANY tab: done `decide_action` commands of this chat.
        decidedProposalIds: [] as string[],
        /** MYAC-1269 — the two sources `decidedProposalIds` merges: my own commands, the row's decisions. */
        myDecidedProposalIds: [] as string[],
        rowDecidedProposalIds: [] as string[],
        /** MYAC-1269 — the conversation's decision rows, by proposal id: who settled what. */
        rowDecisions: {} as Record<string, ChatDecision>,
        /** MYAC-1269 — the conversation on screen stopped being readable (un-shared, left): said once. */
        accessLostChatId: null as string | null,
        runBaseSeq: null as number | null,
        // An i18n key shown under the conversation once (e.g. « you stopped this answer »).
        turnNotice: null as string | null,
        projectUnsubs: [] as Array<() => void>,
        commandUnsubs: {} as Record<string, () => void>,
    }),

    getters: {
        hasActiveSession(): boolean {
            return this.sessionId !== null
        },
        /**
         * MYAC-1522 (B3) — the opening this conversation's FIRST turn carried, the
         * one the agent answered: a page can show it in place of its own current
         * opening (a welcome letter's reply lands on a project chat whose opening is
         * its Pilotage summary; a Pilotage summary may have been rewritten since).
         * The raw events keep the markers (stripped at render), and a turn just sent
         * is already one of them, so this needs no state of its own.
         */
        conversationOpening(): { message: string } | null {
            const first = this.events.find(e => e.content?.role === 'user')
            let text = first?.content?.parts?.find(p => p.text !== undefined)?.text
            if (!text) return null
            if (text.startsWith(ATTACH_PREFIX)) text = stripAttachMarker(text)
            if (text.startsWith(FOCUS_PREFIX)) text = stripFocusMarker(text)
            const message = readOpeningMarker(text)
            return message ? { message } : null
        },
        messages(): ChatbotMessage[] {
            return this.doneMessages
        },
        streamingMessage(): ChatbotMessage | undefined {
            // Show "waking up" message when creating session
            if (this.isCreatingSession) {
                return {
                    id: 'waking-up',
                    parts: [{ text: i18n.global.t('chat.waking-up-title') }],
                    role: 'model',
                    isUserMessage: false,
                    isStreaming: true,
                    isPartial: false,
                    timestamp: Date.now()
                }
            }
            if (!this.isStreaming) {
                return undefined
            }
            // MYAC-1519 — a project chat shows the answer AS IT IS WRITTEN: the
            // live draft (typed out, see `startTypewriter`) above the step label.
            if (this.projectMode) {
                const text = this.shownDraftText
                return {
                    id: 'streaming',
                    parts: text ? processMessageParts([{ text }], this.chatbotResponseFormat) : [],
                    role: 'model',
                    isUserMessage: false,
                    isStreaming: true,
                    isPartial: false,
                    timestamp: Date.now(),
                }
            }
            if (!this.events || this.events.length === 0) {
                return undefined
            }
            // Streaming is intentionally NOT rendered token-by-token — partial markdown looked
            // janky. While the answer is being generated we only show the "thinking" indicator;
            // the finished answer then appears in full via doneMessages. MYAC-750.
            return {
                id: 'streaming',
                parts: [],
                role: 'model',
                isUserMessage: false,
                isStreaming: true,
                isPartial: false,
                timestamp: Date.now(),
                raw: undefined,
            }
        },
        doneMessages(): ChatbotMessage[] {
            let subject: ChatFocusRef | null = null
            // MYAC-1152 — the two witnesses that a tool-call event carries the turn's
            // answer rather than narration. Derived once per render, from the events
            // themselves: the last renderable model text of each turn, and the calls
            // whose result asked the user to act.
            const turns = turnKeys(this.events)
            const lastTextOfTurn = new Map<string, number>()
            this.events.forEach((event, idx) => {
                if (event.partial) return
                if (event.content?.role === 'user') return
                if (hasAnyRenderableText(event)) lastTextOfTurn.set(turns[idx], idx)
            })
            const pauseCallEvents = pauseCallEventIndices(this.events)
            return this.events
                .filter((event, idx) => {
                    // Filter out events with no content or parts
                    if (!event.content || !event.content.parts) {
                        return false
                    }
                    // Filter out partial events
                    if (event.partial) {
                        return false
                    }
                    // While the current turn is still streaming, collapse its intermediate
                    // agent steps (each think/tool step pushes an event) into the single live
                    // "thinking/reading" indicator instead of stacking a bubble per step (which
                    // left growing white gaps). The user's own message still shows, and the
                    // agent's answer renders once the turn completes (isStreaming → false).
                    if (this.isStreaming
                        && idx >= this.streamingBaseEventIndex
                        && event.content.role !== 'user') {
                        return false
                    }
                    // Hide the machine-generated sentinels (MYAC-796) — the action-decision
                    // (card click) and the auto-continued follow-up — they're not real
                    // conversation the user typed.
                    if (event.content.role === 'user') {
                        const firstText = event.content.parts.find(p => p.text !== undefined)?.text
                        if (firstText?.startsWith(ACTION_DECISION_PREFIX)
                            || firstText?.startsWith(ACTION_FOLLOWUP_PREFIX)) {
                            return false
                        }
                        // MYAC-1182 — an attach marker rides IN FRONT of any focus
                        // marker (attachMarker + '\n' + (focusMarker + '\n' + text)),
                        // so peel it first: everything below must see what it would
                        // see with no attachment on the turn at all.
                        const afterAttach = firstText?.startsWith(ATTACH_PREFIX)
                            ? stripAttachMarker(firstText)
                            : firstText
                        // MYAC-919 — a focus marker with nothing behind it has no
                        // bubble to draw: the marker is stripped at render, so it
                        // would leave the empty padded one MYAC-882 got rid of.
                        if (afterAttach?.startsWith(FOCUS_PREFIX)
                            && !stripFocusMarker(afterAttach).trim()) {
                            return false
                        }
                        // MYAC-1182 — same case for an attach marker carrying nothing
                        // (no focus, no typed text) once IT is stripped.
                        if (firstText?.startsWith(ATTACH_PREFIX) && !afterAttach?.trim()) {
                            return false
                        }
                    }
                    // A tool-call event's text is leaked reasoning or narration
                    // (MYAC-802) UNLESS the model stopped there — in which case it is
                    // the turn's answer, riding along with the card that ends the turn
                    // (MYAC-1152). Note this drops the EVENT: its text is all there is
                    // to render, the call and response parts having no bubble of their own.
                    if (event.content.role !== 'user'
                        && isToolCallEvent(event)
                        && !rendersTurnAnswer(idx, turns[idx], lastTextOfTurn, pauseCallEvents)) {
                        return false
                    }
                    // Filter out events with no visible text at all (thinking parts,
                    // tool calls, and empty/whitespace-only text) — they'd render as
                    // empty padded bubbles that accumulate as blank lines (MYAC-882).
                    if (event.content?.parts.every(part => !hasRenderableText(part))) {
                        return false
                    }

                    return true
                })
                // `subject` walks forward with the conversation: a user turn sets
                // it from its own marker (or clears it — a plain question is about
                // nothing in particular), and the model's answer inherits whatever
                // the question carried.
                .map(event => {
                    // Keep only parts with visible text (drops thinking parts and
                    // empty/whitespace-only text parts).
                    const filteredParts = event.content!.parts
                        .filter(hasRenderableText)
                        // MYAC-919 / MYAC-1182 — the markers are plumbing, not
                        // something the user wrote: strip them at RENDER time (attach
                        // first — it rides outermost — then focus) so the raw event
                        // still matches what the server was sent, and any history
                        // replay of it renders clean too.
                        .map(part => {
                            if (part.text === undefined) return part
                            let text = part.text
                            if (text.startsWith(ATTACH_PREFIX)) text = stripAttachMarker(text)
                            if (text.startsWith(FOCUS_PREFIX)) text = stripFocusMarker(text)
                            // MYAC-1313 — the Pilotage first-turn marker.
                            if (text.startsWith(OPENING_PREFIX)) text = stripOpeningMarker(text)
                            return text === part.text ? part : { ...part, text }
                        })
                        .filter(hasRenderableText)
                    const processedParts = processMessageParts(filteredParts, this.chatbotResponseFormat)

                    // MYAC-1182 — per-message, unlike `subject`: a file summary chip
                    // belongs to the turn that attached the files, not to every turn
                    // after it (the transcript is the record of what happened when).
                    let attachments: ChatAttachmentMarkerFile[] | null = null
                    if (event.content!.role === 'user') {
                        const firstText = event.content!.parts.find(p => p.text !== undefined)?.text
                        const afterAttach = firstText?.startsWith(ATTACH_PREFIX)
                            ? stripAttachMarker(firstText)
                            : firstText
                        subject = readFocusMarker(afterAttach)
                        attachments = readAttachMarker(firstText)?.files ?? null
                    }

                    // MYAC-1519 — this tab's own message, not yet in the conversation.
                    const pending = event.id?.startsWith(PENDING_EVENT_PREFIX)
                        ? this.pendingCommands.find(p => `${PENDING_EVENT_PREFIX}${p.commandId}` === event.id)
                        : undefined
                    const refused = pending?.status === 'rejected' || pending?.status === 'stalled'

                    return {
                        ...(pending ? {
                            pending: !refused,
                            rejectedReasonKey: refused ? conversationReasonI18nKey(pending.reasonKey) : null,
                            commandId: pending.commandId,
                        } : {}),
                        id: event.id,
                        parts: processedParts,
                        role: event.content!.role,
                        isUserMessage: event.content!.role === 'user',
                        attachments,
                        isStreaming: false,
                        isPartial: false,
                        timestamp: event.timestamp || Date.now(),
                        focus: subject,
                        raw: event,
                        authorUid: typeof event.authorUid === 'string' ? event.authorUid : this.impliedAuthorUid(event),
                    }
                })
        },
        thinkingMessages(): Event[] {
            return this.events.filter(event => event.content?.parts.some(part => part.thought === true))
        },
        /**
         * MYAC-1269 — a question written before the conversation was shared carries
         * no recorded author (the backend started stamping one with sharing). Only
         * the owner could write then, so to a participant it is the owner's: named
         * like any colleague's question. To the owner it stays unnamed, like their
         * own. Only transcript ROWS qualify — a message this tab is sending is mine.
         */
        impliedAuthorUid(): (event: Event) => string | null {
            const owner = this.projectMode ? (this.chatRow?.owner_uid ?? '') : ''
            const me = currentUid()
            return (event: Event) => {
                if (!owner || owner === me) return null
                if (event.content?.role !== 'user' || !event.id.startsWith(ROW_EVENT_PREFIX)) return null
                return owner
            }
        },
        partialMessages(): Event[] {
            return this.events.filter(event => event.partial === true)
        },
        functionCallMessages(): Event[] {
            return this.events.filter(event => event.content?.parts.some(part => part.functionCall !== undefined))
        },
        // The tool the assistant is calling right now — the latest function call while streaming.
        // Drives the live "Reading the CAPA list…" activity indicator. Null when idle.
        activeToolCall(): FunctionCall | null {
            if (!this.isStreaming) return null
            // Only this turn's events — otherwise a previous turn's tool call would flash
            // before the current turn's events arrive (and on turns that call no tools).
            const turnEvents = this.events.slice(this.streamingBaseEventIndex)
            for (let i = turnEvents.length - 1; i >= 0; i--) {
                const parts = turnEvents[i].content?.parts ?? []
                for (let j = parts.length - 1; j >= 0; j--) {
                    if (parts[j].functionCall) return parts[j].functionCall as FunctionCall
                }
            }
            return null
        },
        // MYAC-796 — action proposals awaiting the user's confirmation. An action tool
        // (e.g. trigger_register_refresh) returns status="approval_required"; we surface
        // each unresolved one as a card. De-duped by proposal_id; resolved ones drop out.
        approvalProposals(): ActionProposal[] {
            const seen = new Set<string>()
            const out: ActionProposal[] = []
            for (const event of this.events) {
                for (const part of event.content?.parts ?? []) {
                    const resp = part.functionResponse?.response as ActionApprovalResponse | undefined
                    if (!resp || resp.status !== 'approval_required' || !resp.proposal_id) continue
                    if (this.resolvedProposalIds.includes(resp.proposal_id)) continue
                    if (this.decidedProposalIds.includes(resp.proposal_id)) continue
                    if (seen.has(resp.proposal_id)) continue
                    seen.add(resp.proposal_id)
                    out.push({ eventId: event.id, proposal: resp })
                }
            }
            return out
        },
        /**
         * MYAC-1269 — the proposals someone ELSE settled, in a shared conversation.
         * The card stays in the transcript with « Acceptée par Marc » where its
         * buttons were: a reader must see who committed the project to what. The
         * person's own decisions keep vanishing, as they always did — the
         * assistant's follow-up says what came of them. One per proposal id, at the
         * event that raised it.
         */
        settledProposals(): SettledProposal[] {
            if (!this.projectMode) return []
            const me = currentUid()
            const seen = new Set<string>()
            const out: SettledProposal[] = []
            this.events.forEach((event, eventIndex) => {
                for (const part of event.content?.parts ?? []) {
                    const resp = part.functionResponse?.response as ActionApprovalResponse | undefined
                    if (!resp || resp.status !== 'approval_required' || !resp.proposal_id) continue
                    const decision = this.rowDecisions[resp.proposal_id]
                    if (!decision || !decision.decidedBy || decision.decidedBy === me) continue
                    if (seen.has(resp.proposal_id)) continue
                    seen.add(resp.proposal_id)
                    out.push({ eventId: event.id, eventIndex, proposal: resp, decision })
                }
            })
            return out
        },
        // MYAC-802 — clarifying-question card groups from ask_user_questions. Every group
        // in the session renders (answered ones collapsed read-only); a group is answered
        // once the user has SENT something after it — the answer itself is a plain visible
        // message, so no sentinel/resolution protocol is needed.
        // MYAC-1152 — "sent something" means a real user message: ADK stamps
        // `role: 'user'` on tool results too, so a tool call made after the card would
        // otherwise collapse it read-only before the user could touch it.
        questionGroups(): QuestionGroup[] {
            const seen = new Set<string>()
            const out: QuestionGroup[] = []
            this.events.forEach((event, eventIndex) => {
                for (const part of event.content?.parts ?? []) {
                    const resp = part.functionResponse?.response as QuestionsPendingResponse | undefined
                    if (!resp || resp.status !== 'questions_pending' || !resp.group_id) continue
                    if (!Array.isArray(resp.questions) || resp.questions.length === 0) continue
                    if (seen.has(resp.group_id)) continue
                    seen.add(resp.group_id)
                    const answered = this.events
                        .slice(eventIndex + 1)
                        .some(isRealUserMessage)
                    out.push({ eventId: event.id, eventIndex, group: resp, answered })
                }
            })
            return out
        },
        // MYAC-1162 — documents the assistant opened in this conversation.
        //
        // Matched on the tool NAME, then `status === 'success'`, then
        // `result.action === 'created'`. All three are load-bearing: one tool creates,
        // edits, renames and deletes, so the name alone would card every edit; a
        // refused write comes back `status: 'error'` with an `error_message` and no
        // result at all, and a card there would point at a document never written.
        //
        // `result` is a NESTED object, and that is the whole history of this getter:
        // the first version read `ok` and `action` off the top level, because that is
        // what the apply layer returns and what its docstring promised the tool passes
        // through untouched. The wrapper reshapes. Nothing matched, no card ever
        // rendered, and the unit tests agreed with the bug because their fixture was
        // built from the same wrong belief. The fixture now comes from a real session.
        //
        // De-duped by doc_id as cheap insurance, not because a replay is expected: this
        // store never GETs a stored session (it only creates one and streams turns), so
        // `events` is filled from the live SSE stream alone — which is also why the read
        // below is camelCase only. A reload starts a new conversation.
        authoredDocumentCards(): AuthoredDocumentCard[] {
            const seen = new Set<string>()
            const out: AuthoredDocumentCard[] = []
            this.events.forEach((event, eventIndex) => {
                for (const part of event.content?.parts ?? []) {
                    if (part.functionResponse?.name !== WRITE_AUTHORED_DOCUMENT_TOOL) continue
                    const resp = part.functionResponse.response as AuthoredDocumentWriteResponse | undefined
                    if (!resp || resp.status !== 'success') continue
                    const result = resp.result
                    if (!result || result.action !== 'created') continue
                    if (!result.doc_id || seen.has(result.doc_id)) continue
                    seen.add(result.doc_id)
                    out.push({
                        eventId: event.id,
                        eventIndex,
                        docId: result.doc_id,
                        title: result.title ?? '',
                        revision: result.revision ?? 1,
                    })
                }
            })
            return out
        },
        // MYAC-883 — the conversation's language, as stamped by the chatbot on card
        // payloads (detected from the user's latest message, sticky). Single source
        // of truth shared by the model's prose, the card chrome and the decision
        // sentinels. Null until any stamped card exists → app locale.
        conversationLanguage(): 'french' | 'english' | null {
            for (let i = this.events.length - 1; i >= 0; i--) {
                for (const part of this.events[i].content?.parts ?? []) {
                    const lang = (part.functionResponse?.response as { language?: string } | undefined)?.language
                    if (lang === 'french' || lang === 'english') return lang
                }
            }
            return null
        },
        /**
         * MYAC-1519 — the step the server says the turn is on (project chat), as a
         * `chat.activity.*` spec for `translateActivity`. Null when no live draft.
         */
        liveActivity(): ActivitySpec | null {
            if (!this.projectMode || !this.isStreaming) return null
            const key = this.liveDraft?.label_key
            if (!key) return null
            return { key: key.replace(/^chat\.activity\./, ''), args: { ...(this.liveDraft?.label_args ?? {}) } }
        },
        /** The error line's sentence: a failed command names what happened, else the generic line (MYAC-1522). */
        errorMessageKey(): string {
            if (this.error?.startsWith(COMMAND_FAILED_PREFIX)) {
                return conversationErrorI18nKey(this.error.slice(COMMAND_FAILED_PREFIX.length))
            }
            return 'chat.error-line'
        },
        /** The command a Stop would cancel: the running turn's, else this tab's own. */
        stopTargetCommandId(): string | null {
            if (!this.projectMode) return null
            if (turnRowIsRunning(this.chatRow) && this.chatRow?.run_command_id) return this.chatRow.run_command_id
            const mine = [...this.pendingCommands].reverse()
                .find(p => ACTIVE_COMMAND_STATUSES.has(p.status) && p.status !== 'requested')
            return mine?.commandId ?? null
        },
        /**
         * MYAC-1269 — what the person may do in the project conversation on screen.
         * `owner` for their own; `write` / `read` on one shared with them; null
         * while the row is unknown (a conversation not yet written, or not a
         * project chat) — the composer then behaves as before.
         */
        conversationRight(): ConversationRight {
            if (!this.projectMode || !this.chatRow) return null
            return conversationRightOf(this.chatRow, currentUid())
        },
        /** The conversation on screen is shared with the person read-only: no composer. */
        isReadOnlyConversation(): boolean {
            return this.conversationRight === 'read'
        },
        /** Is there a project-chat turn the Stop control can cancel right now? */
        turnStoppable(): boolean {
            return this.projectMode && this.isStreaming && this.stopTargetCommandId !== null
        },
        chatbotName(): string | null {
            const appStore = useAppStore()
            // A per-context override (e.g. the CAPA chat) wins over the global config name.
            return this.appNameOverride ?? appStore.chatbotName
        },
        chatbotResponseFormat(): 'markdown' | 'json_markdown' {
            const appStore = useAppStore()
            return appStore.chatbotResponseFormat || 'markdown'
        },
    },

    actions: {
        /**
         * Extract error message from malformed JSON string
         * Attempts to extract error information even when JSON is incomplete
         */
        extractErrorFromMalformedJson(jsonString: string): string | null {
            // Try to extract error message from malformed JSON
            // Pattern: {"error": "message" or {"error":"message"
            const errorMatch = jsonString.match(/"error"\s*:\s*"([^"]*)/)
            if (errorMatch && errorMatch[1]) {
                return errorMatch[1]
            }
            // Try without quotes: {"error": message
            const errorMatch2 = jsonString.match(/"error"\s*:\s*([^,}]+)/)
            if (errorMatch2 && errorMatch2[1]) {
                return errorMatch2[1].trim().replace(/^["']|["']$/g, '')
            }
            return null
        },

        /**
         * Handle error from SSE stream.
         * MYAC-882 — the raw payload is logged for debugging but never rendered: the UI
         * shows a generic friendly message with a retry choice (see ChatInterface).
         */
        handleStreamError(errorMessage: string): void {
            this.error = errorMessage
            this.isStreaming = false
            this.isLoading = false

            logger.error('SSE stream error:', errorMessage)
        },

        /** Dismiss the current error without sending anything (the "No" choice). */
        dismissError(): void {
            this.error = null
        },

        /**
         * MYAC-882 — the "Yes, try again" choice on the error card. Sends a hidden
         * follow-up (the chatbot strips the prefix, the front hides it from the
         * transcript — same mechanism as the MYAC-796 auto-continue) asking the agent
         * to pick the conversation back up, so the user never retypes their question.
         */
        async retryAfterError(): Promise<void> {
            const rawLocale = i18n.global.locale
            const locale = typeof rawLocale === 'string' ? rawLocale : rawLocale.value
            const instruction = locale?.startsWith('fr')
                ? 'Ma dernière demande a échoué à cause d\'une erreur temporaire. Reprends la conversation et réponds à mon dernier message. S\'il n\'y a rien à reprendre, demande-moi comment tu peux aider.'
                : 'My last request failed because of a temporary error. Pick the conversation back up and answer my last message. If there is nothing to answer, ask me how you can help.'
            await this.sendMessage(`${ACTION_FOLLOWUP_PREFIX} ${instruction}`)
        },

        /**
         * Stop the active SSE stream gracefully
         * Sets cancellation flag and cancels the reader
         */
        async stopStream(): Promise<void> {
            if (!this.isStreaming) {
                return
            }

            logger.debug('Stopping active SSE stream')
            this.streamCancelled = true

            if (this.activeReader) {
                try {
                    await this.activeReader.cancel()
                } catch (error) {
                    // Reader might already be closed/cancelled, which is fine
                    logger.debug('Error cancelling reader (may already be closed):', error)
                }
            }
        },
        /**
         * Get Cloud Run service URL from app store config
         */
        async generateServiceUrl(): Promise<string> {
            if (this.baseUrl) {
                return this.baseUrl
            }

            // Dev-only: point the chat at a locally running ADK server
            // (e.g. VITE_CHATBOT_URL_OVERRIDE=http://localhost:8010 yarn dev).
            const overrideUrl = getChatbotUrlOverride()
            if (overrideUrl) {
                this.baseUrl = overrideUrl
                logger.debug('Using chatbot URL from VITE_CHATBOT_URL_OVERRIDE:', overrideUrl)
                return overrideUrl
            }

            const appStore = useAppStore()
            const configUrl = appStore.chatbotUrl

            if (!configUrl) {
                throw new Error('Chatbot URL not configured. Please ensure system/config document exists with chatbot_url field.')
            }

            this.baseUrl = configUrl
            logger.debug('Using chatbot URL from config:', configUrl)
            return configUrl
        },

        /**
         * Get Firebase auth ID token for API authentication
         */
        async getAuthToken(): Promise<string> {
            const auth = getAuth()
            const user = auth.currentUser

            if (!user) {
                throw new Error('User not authenticated')
            }

            try {
                const token = await user.getIdToken()
                return token
            } catch (error) {
                logger.error('Failed to get auth token', error)
                throw error
            }
        },

        /**
         * Create a new chat session
         * POST /apps/{chatbot_name}/users/{encoded_user_id}/sessions
         */
        async createSession(userId?: string): Promise<void> {
            if (this.isCreatingSession) {
                return
            }

            try {
                this.isCreatingSession = true
                const usersStore = useUsersStore()

                if (!usersStore.user) {
                    throw new Error('User not authenticated')
                }

                // Set project ID from the CHAT'S scope when creating session —
                // the route's project inside one, the account's QMS on global
                // surfaces (MYAC-1327). Never the sticky currentProjectId.
                const currentProjectId = useChatScope().scopeProjectId.value || ''
                if (!this.projectId || this.projectId !== currentProjectId) {
                    this.projectId = currentProjectId || null
                    logger.debug('Setting chat project ID on session creation:', this.projectId)
                }

                const uid = usersStore.uid
                const baseUrl = await this.generateServiceUrl()
                const token = await this.getAuthToken()

                // Use the caller-supplied user scope when given, so the session is created
                // under the SAME user_id that run_sse will use. `this.projectId` can change
                // between createSession and run_sse (a resetSession nulls it), which would
                // otherwise create the session as `projects\<id>` but look it up as
                // `users\<uid>` → "Session not found" (MYAC-750).
                const rawUserId = userId
                    ?? (this.projectId ? `projects\\${this.projectId}` : `users\\${uid}`)
                const encodedUserId = encodeURIComponent(rawUserId)
                const url = `${baseUrl}/apps/${this.chatbotName}/users/${encodedUserId}/sessions`

                // Build request body with projects array if project ID exists
                const requestBody: { projects?: string[] } = {}
                if (currentProjectId && currentProjectId.trim() !== '') {
                    requestBody.projects = [currentProjectId]
                }

                logger.debug('Creating session:', url, requestBody)

                let response: Response
                try {
                    response = await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(requestBody),
                    })
                } catch (fetchError) {
                    // Check if this is a CORS error
                    if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
                        throw new Error(
                            'CORS error: The chatbot service is not returning required CORS headers. ' +
                            'Ensure the backend service returns Access-Control-Allow-Origin headers.'
                        )
                    }
                    throw fetchError
                }

                if (!response.ok) {
                    const errorText = await response.text()
                    throw new Error(`Failed to create session: ${response.status} ${errorText}`)
                }

                const data = await response.json()

                // Parse session ID from response
                // Assuming the response contains a session_id field or similar
                if (data.id) {
                    this.sessionId = data.id
                } else {
                    throw new Error('Session ID not found in response')
                }

                logger.info('Session created:', this.sessionId)
            } catch (error) {
                // MYAC-882 — log the raw error; the UI only shows the generic error card.
                logger.error('Error creating session', error)
                this.error = (error as Error)?.message || 'Failed to create chat session'
                throw error
            } finally {
                this.isCreatingSession = false
            }
        },

        /**
         * Send a message and handle SSE streaming response
         * POST /run_sse
         */
        async sendMessage(message: string): Promise<void> {
            if (!message.trim()) {
                return
            }
            // MYAC-919 — the attached reference travels in front of the message;
            // the agent strips it, and so does the transcript (doneMessages). The
            // event we push carries the SAME text that goes on the wire, so what
            // the server stores and what we render never disagree about what was
            // said.
            // A machine sentinel is NOT the user asking about the attached item,
            // and prefixing one breaks it in two places at once (PR #480 review):
            // the backend's `bac_handle_action_decision` reads the raw text in a
            // before_agent callback — before anything strips the marker — so its
            // `startswith(__ACTION_DECISION__)` check fails and the approve/decline
            // click is silently dropped; and the transcript filter below misses the
            // same prefix, leaking the sentinel into the conversation as a bubble.
            // The attachment is left pending: it belongs to the user's next real
            // message, not to a card click that happened in between.
            const isSentinel = isMachineSentinel(message)

            // MYAC-1182 — attachments block the send until every upload has
            // resolved (spec BUILD 1): a file still in flight, or one that failed
            // and was neither retried nor removed, must not go out silently
            // text-only. A machine sentinel (card click, auto follow-up) never
            // carries attachments — same reasoning as the focus marker below.
            //
            // Checked BEFORE `takeFocus()` on purpose: that call CONSUMES the
            // pending focus (one message, one attachment). Blocking after taking
            // it would drop the focus chip on a send that never actually went
            // out — the attempt must leave every bit of compose state untouched.
            const attachmentsStore = useChatAttachmentsStore()
            if (!isSentinel && attachmentsStore.blocksSend) {
                logger.warn('sendMessage: blocked - an attachment is still uploading or failed')
                return
            }

            const focus = isSentinel ? null : this.takeFocus()
            const attachBatch = isSentinel ? null : attachmentsStore.takeMarkerBatch()

            let wireMessage = focus ? `${focusMarker(focus)}\n${message}` : message
            if (attachBatch) {
                wireMessage = `${attachMarker(attachBatch)}\n${wireMessage}`
            }

            // MYAC-1138: the count belongs to a message a HUMAN typed. Retry and
            // approve re-enter through this same function carrying a sentinel, and
            // counting those would inflate engagement with the assistant's own turns.
            if (!isMachineSentinel(wireMessage)) {
                try {
                    // Fix-wave (whole-branch review, C1): `this.projectId` is only ever set
                    // INSIDE sendMessage/createSession, below this line — so the FIRST message
                    // of every session (and every project/tenant switch, which nulls it via
                    // resetSession) always read null here and got recorded under `_tenant`.
                    // Fall back to the project actually selected on screen, same idiom as
                    // chatbot.ts's requestRepairReassessment and chatPanel.ts's open().
                    // MYAC-1327: the chat's own scope first (on the dashboard that is
                    // the QMS the session will actually bind to), the on-screen
                    // project as last resort.
                    trackAction(CsmAction.ChatMessageSent, {
                        projectId: this.projectId || useChatScope().scopeProjectId.value
                            || useProjectsStore().currentProjectId || undefined,
                    })
                } catch { /* tracking never breaks a send */ }
            }

            // MYAC-1519 (option B) — a PROJECT chat is a command the server runs on
            // its own; the tab only follows the conversation. Only the no-project
            // chat (the `users\\{uid}` scope, decision D7) still streams below.
            // Local rail (MYAC-1398): with VITE_CHATBOT_DIRECT_TURNS the project
            // chat streams straight to the overridden local server instead — the
            // deployed service would otherwise take the command (agentEndpoint.ts).
            const scopeProjectId = useChatScope().scopeProjectId.value
            if (scopeProjectId && !chatbotDirectTurns()) {
                await this.sendProjectMessage(scopeProjectId, wireMessage)
                return
            }

            // ── Legacy path: the no-project chat, `createSession` + `/run_sse` ──
            // Stop any active stream before starting a new one
            if (this.isStreaming) {
                logger.warn('Message already streaming, stopping current stream before starting new one')
                await this.stopStream()
            }

            // Clear any previous error and reset cancellation flag when sending a new message
            this.error = null
            this.streamCancelled = false

            try {
                this.isStreaming = true
                // Mark where this turn starts so activeToolCall ignores previous turns' calls.
                this.streamingBaseEventIndex = this.events.length
                this.isLoading = true
                const usersStore = useUsersStore()

                if (!usersStore.user) {
                    throw new Error('User not authenticated')
                }

                const uid = usersStore.uid
                const baseUrl = await this.generateServiceUrl()
                const token = await this.getAuthToken()

                // Add user message as an event
                const userEvent: Event = {
                    id: Date.now().toString(),
                    content: {
                        parts: [{ text: wireMessage }],
                        role: 'user',
                    },
                }
                this.events.push(userEvent)

                // Set project ID from the CHAT'S scope when sending first message or if
                // the scope changed (MYAC-1327 — route project, else the account's QMS)
                const currentProjectId = useChatScope().scopeProjectId.value || ''
                // Bind the user scope for this whole exchange ONCE — createSession and
                // run_sse must agree on user_id even if this.projectId mutates in between.
                const userId = currentProjectId ? `projects\\${currentProjectId}` : `users\\${uid}`
                if (!this.projectId || this.projectId !== currentProjectId) {
                    this.projectId = currentProjectId || null
                    // Project changed — force new session so it's scoped correctly
                    this.sessionId = null
                    logger.debug('Setting chat project ID:', this.projectId)
                }

                if (!this.sessionId) {
                    // Auto-create session if none exists
                    logger.debug('Creating session because none exists')
                    await this.createSession(userId)
                } else {
                    logger.debug('Using existing session:', this.sessionId)
                }

                // Prepare request body for run_sse
                // According to ADK REST API: https://google.github.io/adk-docs/api-reference/rest/#run_sse
                // new_message must be a Content object with parts array and role
                const requestBody = {
                    app_name: this.chatbotName,
                    user_id: userId,
                    session_id: this.sessionId,
                    new_message: userEvent.content!,
                    streaming: true,
                }

                const url = `${baseUrl}/run_sse`

                logger.debug('Sending message:', url, requestBody)

                let response: Response
                try {
                    response = await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(requestBody),
                    })
                } catch (fetchError) {
                    // Check if this is a CORS error
                    if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
                        throw new Error(
                            'CORS error: The chatbot service is not returning required CORS headers. ' +
                            'Ensure the backend service returns Access-Control-Allow-Origin headers.'
                        )
                    }
                    throw fetchError
                }

                if (!response.ok) {
                    const errorText = await response.text()
                    throw new Error(`Failed to send message: ${response.status} ${errorText}`)
                }

                // Handle SSE stream
                const reader = response.body?.getReader()
                const decoder = new TextDecoder()

                if (!reader) {
                    throw new Error('Response body is not readable')
                }

                // Store reader reference for potential cancellation
                this.activeReader = reader

                let buffer = ''

                while (true) {
                    // Check if stream was cancelled
                    if (this.streamCancelled) {
                        logger.debug('Stream was cancelled, exiting read loop')
                        break
                    }

                    const { done, value } = await reader.read()

                    // Check again after read (in case cancellation happened during read)
                    if (this.streamCancelled) {
                        logger.debug('Stream was cancelled after read, exiting read loop')
                        break
                    }

                    if (done) {
                        break
                    }

                    buffer += decoder.decode(value, { stream: true })
                    const lines = buffer.split('\n')
                    buffer = lines.pop() || '' // Keep incomplete line in buffer

                    for (const line of lines) {
                        // Check cancellation flag in inner loop too
                        if (this.streamCancelled) {
                            break
                        }

                        if (line.startsWith('data: ')) {
                            try {
                                const data = line.slice(6) // Remove 'data: ' prefix

                                if (data === '[DONE]') {
                                    // Stream complete
                                    break
                                }

                                const parsed = JSON.parse(data)

                                // Check if parsed object contains an error field
                                if (parsed && typeof parsed === 'object' && 'error' in parsed && parsed.error) {
                                    const errorMsg = typeof parsed.error === 'string' ? parsed.error : String(parsed.error)
                                    this.handleStreamError(errorMsg)
                                    break // Stop streaming on error
                                }

                                // Log event structure for debugging
                                logger.debug('SSE event received:', JSON.stringify(parsed, null, 2))

                                // Create properly typed Event object, preserving thought property on parts
                                // Per Gemini API docs: parts may have thought: true for thinking parts
                                this.events.push(parsed as Event)
                            } catch (parseError) {
                                // Try to extract error message from malformed JSON
                                const data = line.slice(6) // Remove 'data: ' prefix
                                const extractedError = this.extractErrorFromMalformedJson(data)

                                if (extractedError) {
                                    // Found an error message in malformed JSON
                                    this.handleStreamError(extractedError)
                                    break // Stop streaming on error
                                } else {
                                    // Could not extract error, log and continue
                                    logger.warn('Failed to parse SSE data:', line, parseError)
                                    // Still set a generic error to inform the user
                                    this.handleStreamError('Failed to parse response from server')
                                    break
                                }
                            }
                        }
                    }
                }

                // MYAC-1382 — a cut stream closes cleanly; without this the turn ended
                // with no bubble and no error. The generic error card's retry asks the
                // chat to pick the conversation back up from what the session kept.
                // Only while this stream is still the active one: a newer send has
                // already stopped it and reset the turn markers to ITS own turn.
                if (this.activeReader === reader && !this.streamCancelled && !this.error
                    && !turnLeftSomethingToShow(this.events, this.streamingBaseEventIndex)) {
                    this.handleStreamError(TURN_ENDED_WITHOUT_ANSWER)
                }

                logger.debug('Message stream completed')
            } catch (error) {
                // MYAC-882 — log the raw error; the UI only shows the generic error card.
                logger.error('Error sending message', error)
                this.error = (error as Error)?.message || 'Failed to send message'
            } finally {
                // Clean up reader reference and reset cancellation flag
                this.activeReader = null
                this.streamCancelled = false
                this.isStreaming = false
                this.isLoading = false
            }
        },

        // ─────────────────────────────────────────────────────────────────────
        // MYAC-1519 — the project path: write commands, follow the views.
        // ─────────────────────────────────────────────────────────────────────

        /**
         * Send one wire message in a project chat: open the conversation if none
         * is (minting its chat id), write a `send_message` command, show the
         * message at once, then follow the command.
         */
        async sendProjectMessage(projectId: string, wireMessage: string): Promise<void> {
            this.error = null
            this.turnNotice = null
            if (!(this.projectMode && this.projectId === projectId && this.sessionId)) {
                this.openConversation(projectId, mintChatId(projectId))
            }
            await this.dispatchSendCommand(projectId, wireMessage)
        },

        async dispatchSendCommand(projectId: string, wireMessage: string): Promise<void> {
            const chatId = this.sessionId
            if (!chatId) return
            const commandId = mintCommandId(projectId)
            this.pendingCommands.push({
                commandId,
                kind: 'send',
                text: wireMessage,
                status: 'requested',
                baseSeq: this.nextRowSeq(),
                echoed: false,
                at: Date.now(),
            })
            this.rebuildProjectEvents()
            try {
                await sendMessageCommand(projectId, {
                    chat_id: chatId,
                    app_name: this.chatbotName ?? '',
                    text: wireMessage,
                }, { commandId })
            } catch (error) {
                logger.error('chat: send_message command could not be written', error)
                this.onCommandUpdate({ commandId, status: 'rejected', reasonKey: 'dispatch' })
                return
            }
            this.followCommand(projectId, commandId)
        },

        /**
         * Send a refused (or undelivered) message again, as a new command.
         *
         * A refused command is over: it is forgotten. An undelivered one (still
         * `requested`) may yet be admitted, which would run — and bill — the turn
         * twice: it is cancelled first (best effort), and kept followed, hidden, so
         * that if it is admitted anyway its user row is matched to IT and not taken
         * for the resend's echo.
         */
        async resendMessage(commandId: string): Promise<void> {
            const pending = this.pendingCommands.find(p => p.commandId === commandId && p.kind === 'send')
            if (!pending || pending.superseded || !this.projectId || this.isStreaming || this.isLoading) return
            const projectId = this.projectId
            if (pending.status === 'stalled' || pending.status === 'requested') {
                pending.superseded = true
                cancelCommand(projectId, commandId).catch((error: unknown) => {
                    logger.warn('chat: cancel of an undelivered message could not be written', error)
                })
            } else {
                this.dropCommand(commandId)
            }
            this.error = null
            this.turnNotice = null
            await this.dispatchSendCommand(projectId, pending.text)
        },

        /** The seq the next conversation row will carry (rows of a new turn start here). */
        nextRowSeq(): number {
            return this.messageRows.reduce((max, r) => Math.max(max, r.seq), -1) + 1
        },

        /** Follow a command doc this tab wrote; the stop function is kept in state. */
        followCommand(projectId: string, commandId: string): void {
            this.commandUnsubs[commandId]?.()
            this.commandUnsubs[commandId] = watchCommand(projectId, commandId, state => this.onCommandUpdate(state))
            admissionTimers.set(commandId, setTimeout(() => {
                admissionTimers.delete(commandId)
                const p = this.pendingCommands.find(c => c.commandId === commandId)
                if (p?.status !== 'requested') return
                // Not a verdict: the listener stays, and a late admission puts the
                // message back on its way. Until then the person can resend.
                p.status = 'stalled'
                p.reasonKey = 'dispatch'
                this.rebuildProjectEvents()
            }, COMMAND_ADMISSION_MS))
            this.syncProjectRunState()
        },

        /** Stop following a command and forget it. */
        dropCommand(commandId: string): void {
            this.commandUnsubs[commandId]?.()
            delete this.commandUnsubs[commandId]
            const timer = admissionTimers.get(commandId)
            if (timer) clearTimeout(timer)
            admissionTimers.delete(commandId)
            this.pendingCommands = this.pendingCommands.filter(p => p.commandId !== commandId)
        },

        /** What the backend decided about a command this tab sent. */
        onCommandUpdate(state: ConversationCommandState): void {
            const pending = this.pendingCommands.find(p => p.commandId === state.commandId)
            if (!pending) return
            pending.status = state.status
            pending.reasonKey = state.reasonKey
            pending.errorKey = state.errorKey
            if (state.status !== 'requested') {
                const timer = admissionTimers.get(state.commandId)
                if (timer) clearTimeout(timer)
                admissionTimers.delete(state.commandId)
            }
            if (TERMINAL_COMMAND_STATUSES.has(state.status)) {
                commandWaiters.get(state.commandId)?.(state.status)
                commandWaiters.delete(state.commandId)
            }
            const ownCancel = state.status === 'cancelled'
                || (state.status === 'failed' && conversationErrorKey(state.errorKey) === 'cancelled')
            if (pending.superseded && (state.status === 'rejected' || ownCancel)) {
                // The resend replaced it: refused or cancelled, there is nothing to say.
                this.dropCommand(pending.commandId)
            } else if (state.status === 'rejected') {
                if (pending.kind === 'decide') {
                    // The decision never happened: the card is back, with the reason.
                    this.resolvedProposalIds = this.resolvedProposalIds.filter(id => id !== pending.proposalId)
                    this.toastRefusal(state.reasonKey)
                    this.dropCommand(pending.commandId)
                } else if (isMachineSentinel(pending.text)) {
                    // A hidden message has no bubble to carry its reason.
                    this.toastRefusal(state.reasonKey)
                    this.dropCommand(pending.commandId)
                }
                // A visible message stays, refused, with its reason and a resend.
            } else if (state.status === 'done') {
                this.dropCommand(pending.commandId)
            } else if (ownCancel) {
                this.turnNotice = conversationErrorI18nKey('cancelled')
                this.dropCommand(pending.commandId)
            } else if (state.status === 'failed' && pending.kind === 'decide') {
                // The decision did not go through: the card is back, as on a refusal.
                this.resolvedProposalIds = this.resolvedProposalIds.filter(id => id !== pending.proposalId)
                this.toastRefusal('internal')
                this.dropCommand(pending.commandId)
            } else if (state.status === 'failed') {
                this.error = `${COMMAND_FAILED_PREFIX}${conversationErrorKey(state.errorKey)}`
                this.dropCommand(pending.commandId)
            }
            this.rebuildProjectEvents()
        },

        toastRefusal(reasonKey: string | undefined): void {
            useAppStore().toast(
                i18n.global.t('chat.command.not_sent'),
                i18n.global.t(conversationReasonI18nKey(reasonKey)),
                'warn',
            )
        },

        /**
         * Resolve once the command is terminal, or after `ms` with `timeout`
         * (`null`: no limit). A command this tab no longer follows resolves
         * `unknown` — never `done`, which would run follow-ups on a guess.
         */
        waitForCommand(commandId: string, ms: number | null): Promise<string> {
            const pending = this.pendingCommands.find(p => p.commandId === commandId)
            if (!pending) return Promise.resolve('unknown')
            return new Promise(resolve => {
                const timer = ms === null ? null : setTimeout(() => {
                    commandWaiters.delete(commandId)
                    resolve('timeout')
                }, ms)
                commandWaiters.set(commandId, (status) => {
                    if (timer) clearTimeout(timer)
                    resolve(status)
                })
            })
        },

        /**
         * Follow one project conversation from its rows: the conversation row
         * (is a turn running), its messages and cards, and the live draft.
         * Remembers it for this project in the tab (a reload resumes it).
         */
        openConversation(projectId: string, chatId: string): void {
            this.teardownProject()
            this.projectMode = true
            this.projectId = projectId
            this.sessionId = chatId
            this.events = []
            this.error = null
            this.accessLostChatId = null
            rememberChat(projectId, this.chatbotName, chatId)

            const db = useFirestore()
            const rowPath = viewRowPath(projectId, 'chat_sessions', chatId)
            // MYAC-1269 — a refusal on the row or its messages while the conversation
            // is open means the person lost their access to it (un-shared, removed,
            // left from another tab): the conversation is closed, not kept stale.
            const fail = (what: string) => (err: unknown) => {
                logger.error(`chat: ${what} subscription failed`, err)
                if (isPermissionDenied(err)) void this.onConversationAccessLost(projectId, chatId)
            }
            // MYAC-918 — the person has this conversation in view: on opening it, and
            // each time an answer lands while they look at it, `seen_at` moves — the
            // list never says « Nouvelle réponse » for what they just read.
            let seenActivityAt: number | null | undefined
            this.projectUnsubs.push(onSnapshot(doc(db, rowPath), (snap) => {
                this.chatRow = snap.exists() ? (snap.data() as ChatSessionRunRow) : null
                this.syncProjectRunState()
                // Only once there IS an answer to have seen: a row with no
                // `last_activity_at` has nothing the list could call new.
                const activityAt = timeMillis(this.chatRow?.last_activity_at)
                const visible = typeof document === 'undefined' || document.visibilityState === 'visible'
                if (activityAt !== null && visible && activityAt !== seenActivityAt) {
                    seenActivityAt = activityAt
                    // the personal read state exists only with the conversations list
                    if (useFeatureFlagsStore().isConversationsEnabled) void useConversationsStore().markSeen(chatId)
                }
            }, fail('conversation row')))
            this.projectUnsubs.push(onSnapshot(
                query(collection(db, `${rowPath}/messages`), orderBy('seq', 'asc')),
                (snap) => {
                    this.messageRows = snap.docs.map(d => ({ ...(d.data() as Omit<ChatSessionMessageRow, 'id'>), id: d.id }))
                    this.rebuildProjectEvents()
                },
                fail('conversation messages'),
            ))
            this.projectUnsubs.push(onSnapshot(doc(db, `${rowPath}/live/current`), (snap) => {
                this.onLiveDraft(snap.exists() ? (snap.data() as ChatLiveDraft) : null)
            }, fail('live draft')))
            // Approvals decided from another tab or device. Best effort: if the
            // rules refuse the query, this tab's own list still hides its cards.
            let uid = ''
            try { uid = useUsersStore().uid } catch { /* not signed in: no cross-tab decisions */ }
            if (uid) this.projectUnsubs.push(onSnapshot(
                // A `conversation` command carries the message text, so the rule makes
                // it readable by its AUTHOR only (Aymeric, 2026-10-09): a query on this
                // queue must carry `requested_by == uid` or Firestore refuses it as
                // unprovable. So this is "the approvals *I* decided in another tab";
                // another member's decisions arrive through the `decisions` rows below
                // (MYAC-1269). Four equality filters: Firestore merges the automatic
                // single-field indexes, no composite index — and the listener reads
                // only the decisions, never the messages (each command carries its text).
                query(
                    collection(db, conversationCommandsPath(projectId)),
                    where('requested_by', '==', uid),
                    where('payload.chat_id', '==', chatId),
                    where('type', '==', 'decide_action'),
                    where('status', '==', 'done'),
                ),
                (snap) => {
                    this.myDecidedProposalIds = snap.docs
                        .map(d => (d.data() as { payload?: { proposal_id?: string } }).payload?.proposal_id)
                        .filter((id): id is string => typeof id === 'string' && id !== '')
                    this.mergeDecidedProposalIds()
                },
                (err) => logger.warn('chat: decided approvals unavailable', err),
            ))
            // MYAC-1269 — every reader's view of who decided what: the `decisions`
            // rows follow the conversation's own rule, so a participant's decision
            // settles the card for the owner and the other participants too.
            this.projectUnsubs.push(onSnapshot(
                collection(db, `${rowPath}/decisions`),
                (snap) => {
                    const decisions: Record<string, ChatDecision> = {}
                    for (const d of snap.docs) {
                        const decision = decisionFromRow(d.id, d.data() as ChatDecisionRow)
                        if (decision.proposalId) decisions[decision.proposalId] = decision
                    }
                    this.rowDecisions = decisions
                    this.rowDecidedProposalIds = Object.keys(decisions)
                    this.mergeDecidedProposalIds()
                },
                (err) => logger.warn('chat: conversation decisions unavailable', err),
            ))
        },

        /**
         * MYAC-1269 — the person can no longer read the conversation on screen
         * (the owner removed them or stopped sharing, or they left from another
         * tab). The transcript they still see is a stale copy nothing can update,
         * and anything they send comes back `forbidden`: say it once, forget the
         * conversation for this tab, and open a fresh one — as leaving does.
         */
        async onConversationAccessLost(projectId: string, chatId: string): Promise<void> {
            if (!this.projectMode || this.sessionId !== chatId || this.accessLostChatId === chatId) return
            this.accessLostChatId = chatId
            useAppStore().toast(
                i18n.global.t('chat.shared.lost_title'), i18n.global.t('chat.shared.lost'), 'warn',
            )
            forgetChat(projectId, this.chatbotName)
            await this.resetSession()
        },

        /** The union of the decisions this tab's author made and the conversation's own rows. */
        mergeDecidedProposalIds(): void {
            this.decidedProposalIds = [...new Set([...this.myDecidedProposalIds, ...this.rowDecidedProposalIds])]
        },

        /**
         * Bind the chat to a scope (ChatInterface mount / scope change). For this
         * ticket a fresh store starts a new conversation, except the one this tab
         * already had in that project (reload): that one is resumed.
         */
        async resumeProjectChat(projectId: string | null): Promise<void> {
            if (!projectId) {
                if (this.projectMode) await this.resetSession()
                return
            }
            if (this.projectMode && this.projectId === projectId && this.sessionId) return
            if (this.projectMode) await this.resetSession()
            // A no-project conversation still on screen: the component decides.
            if (this.events.length > 0) return
            const remembered = readRememberedChat(projectId, this.chatbotName)
            if (remembered) this.openConversation(projectId, remembered)
        },

        /** Stop every listener and timer of the project path and clear its state. */
        teardownProject(): void {
            for (const stop of this.projectUnsubs) stop()
            for (const stop of Object.values(this.commandUnsubs)) stop()
            clearProjectTimers()
            this.projectUnsubs = []
            this.commandUnsubs = {}
            this.projectMode = false
            this.chatRow = null
            this.messageRows = []
            this.liveDraft = null
            this.draftTarget = ''
            this.shownDraftText = ''
            this.pendingCommands = []
            this.decidedProposalIds = []
            this.myDecidedProposalIds = []
            this.rowDecidedProposalIds = []
            this.rowDecisions = {}
            this.runBaseSeq = null
            this.turnNotice = null
        },

        onLiveDraft(draft: ChatLiveDraft | null): void {
            if (draft) {
                const text = draft.text ?? ''
                const newTurn = this.liveDraft?.turn_id !== undefined && draft.turn_id !== this.liveDraft.turn_id
                if (newTurn || !text.startsWith(this.shownDraftText)) this.shownDraftText = ''
                this.liveDraft = draft
                this.draftTarget = text
                startTypewriter(this)
            } else {
                // The messages are the truth now; the text already shown stays
                // until the run ends, when the final message takes its place.
                this.liveDraft = null
            }
            this.syncProjectRunState()
        },

        /** Rebuild the transcript events from the rows + this tab's unsent messages. */
        /**
         * Rebuild the transcript events from the rows + this tab's unsent messages.
         *
         * Echo: a sent message's user row is the first unclaimed user row with its
         * text from its `baseSeq` on, claimed in send order. Recomputed every time,
         * so a superseded command (see `resendMessage`) claims a row only once the
         * backend has admitted it — its row is then never taken for the resend's.
         *
         * An optimistic copy is drawn where it was sent (before the rows from its
         * `baseSeq` on), not at the tail: a refused message stays above the turns
         * that came after it.
         */
        rebuildProjectEvents(): void {
            if (!this.projectMode) return
            const claimed = new Set<string>()
            const userRows = this.messageRows.filter(r => r.kind === 'message' && r.role === 'user')
            const sends = this.pendingCommands
                .filter(p => p.kind === 'send')
                .sort((a, b) => a.baseSeq - b.baseSeq || a.at - b.at)
            for (const p of sends) {
                const admitted = p.status !== 'requested' && p.status !== 'stalled'
                if (p.superseded && !admitted) {
                    p.echoed = false
                    continue
                }
                const row = userRows.find(r => !claimed.has(r.id) && r.seq >= p.baseSeq && (r.text ?? '') === p.text)
                if (row) claimed.add(row.id)
                p.echoed = row !== undefined
            }
            const optimistic = sends.filter(p => !p.echoed && !p.superseded)
            const events: Event[] = []
            const draw = (p: PendingCommand) => events.push({
                id: `${PENDING_EVENT_PREFIX}${p.commandId}`,
                content: { role: 'user', parts: [{ text: p.text }] },
            } as Event)
            let next = 0
            for (const r of this.messageRows) {
                while (next < optimistic.length && optimistic[next].baseSeq <= r.seq) draw(optimistic[next++])
                events.push(...rowsToEvents([r]))
            }
            while (next < optimistic.length) draw(optimistic[next++])
            this.events = events
            this.syncProjectRunState()
        },

        /**
         * Is a turn running, and where did it start? From the conversation row
         * (bounded by `TURN_STALE_MS`), the live draft (only while the row runs or
         * the draft is fresh, `liveDraftHoldsTurn`), and this tab's own commands
         * still in flight.
         */
        syncProjectRunState(): void {
            if (!this.projectMode) return
            const now = Date.now()
            const active = this.pendingCommands
                .filter(p => ACTIVE_COMMAND_STATUSES.has(p.status) && now - p.at < TURN_STALE_MS)
            const rowRunning = turnRowIsRunning(this.chatRow, now)
            const draftRunning = liveDraftHoldsTurn(this.liveDraft, this.chatRow, now)
            const running = rowRunning || draftRunning || active.length > 0
            this.isLoading = active.some(p => p.status === 'requested' || p.status === 'queued')
            this.isStreaming = running

            if (running) {
                const mine = active[active.length - 1]
                // A turn this tab started begins after the rows it saw, fixed once.
                // A turn found running (reload, second tab, another member's turn on a
                // shared conversation) begins at its question, re-read on every
                // snapshot: the row can say « running » before the question's row is
                // projected, and a base frozen on the previous question hid that
                // question's answer until the turn ended (MYAC-1269).
                if (mine) {
                    if (this.runBaseSeq === null) this.runBaseSeq = mine.baseSeq
                } else {
                    this.runBaseSeq = foundTurnBaseSeq(this.messageRows)
                }
                const base = this.runBaseSeq
                const rowEvents = rowsToEvents(this.messageRows.filter(r => r.seq < base)).length
                this.streamingBaseEventIndex = rowEvents
            } else {
                this.runBaseSeq = null
                this.streamingBaseEventIndex = this.events.length
                // Also drops a leftover draft that arrived while idle: its text
                // must not show as the next turn's answer.
                stopTypewriter()
                this.draftTarget = ''
                this.shownDraftText = ''
            }

            // A running row with no further snapshot still releases the chat when
            // it goes stale; a command with no answer at all does too, and so does a
            // leftover live draft.
            if (runStaleTimer) clearTimeout(runStaleTimer)
            runStaleTimer = null
            if (running) {
                const started = timeMillis(this.chatRow?.run_started_at)
                const draftUpdated = timeMillis(this.liveDraft?.updated_at)
                const expiries = [
                    ...(rowRunning && started !== null ? [started + TURN_STALE_MS] : []),
                    ...(draftRunning && !rowRunning && draftUpdated !== null ? [draftUpdated + LIVE_DRAFT_STALE_MS] : []),
                    ...active.map(p => p.at + TURN_STALE_MS),
                ]
                if (expiries.length) {
                    runStaleTimer = setTimeout(() => this.syncProjectRunState(), Math.max(0, Math.min(...expiries) - now) + 50)
                }
            }
        },

        /**
         * Stop the turn being answered. Project chat: a `cancel` command for the
         * running turn (it stops on the server, for every tab). No-project chat:
         * closes the stream, as before.
         */
        // csm-event: none — no CsmAction exists for a stopped answer yet; adding one is a backend enum change, out of MYAC-1519's scope.
        async stopTurn(): Promise<void> {
            if (!this.projectMode) {
                await this.stopStream()
                return
            }
            const target = this.stopTargetCommandId
            if (!target || !this.projectId) return
            try {
                await cancelCommand(this.projectId, target)
            } catch (error) {
                logger.error('chat: cancel command could not be written', error)
                // not the "message not sent" copy: the message was delivered long ago
                useAppStore().toast(
                    i18n.global.t('chat.command.stop'),
                    i18n.global.t('chat.command.stop_failed'),
                    'warn',
                )
            }
        },

        /**
         * MYAC-919 — attach (or replace) the object the next message is about.
         * Replacing rather than stacking is deliberate: two references on one
         * question is a question about neither, and the chip can only show one.
         */
        setFocus(focus: ChatFocusRef): void {
            this.pendingFocus = focus
            // MYAC-1138: `kind` ONLY. `focus` also carries uid/code/name/label —
            // `label` is documented above as "how the subject is named on
            // screen" (a requirement/document title), i.e. client data.
            try {
                // Fix-wave (whole-branch review, C1): same null-until-first-send gap as
                // `chat_message_sent` above — fall back to the currently selected project.
                trackAction(CsmAction.ChatFocusAttached, {
                    projectId: this.projectId || useChatScope().scopeProjectId.value
                        || useProjectsStore().currentProjectId || undefined,
                    projectMeta: { kind: focus.kind },
                })
            } catch { }
        },
        clearFocus(): void {
            this.pendingFocus = null
        },
        /**
         * Hand the attachment to the message being sent, and drop it — one
         * message, one attachment (Aymeric: "once the user sends a msg that thing
         * is gone"). The agent has the reference in that turn's own text, so the
         * conversation keeps it without the chip lingering over every later
         * question the user may not mean it for.
         */
        takeFocus(): ChatFocusRef | null {
            const focus = this.pendingFocus
            this.pendingFocus = null
            return focus
        },

        // MYAC-1138: `chat_session_reset` must count a RESTART THE USER ASKED
        // FOR ("New session"), never one of this store's own housekeeping resets
        // (project switch, tenant switch, app-name switch) — those would inflate
        // the count every time something else nudges the conversation, not the
        // user. `userInitiated` defaults false so every existing internal caller
        // (unchanged) stays silent; only the "New session" control opts in.
        async resetSession(opts: { userInitiated?: boolean } = {}): Promise<void> {
            // Stop any active stream before resetting
            await this.stopStream()

            const projectId = this.projectId
            // MYAC-1519 — leaving a project conversation stops following it. Only a
            // restart the person asked for forgets it for this tab; a scope switch
            // keeps it, so coming back to that project resumes it.
            if (this.projectMode && opts.userInitiated && projectId) {
                forgetChat(projectId, this.chatbotName)
            }
            this.teardownProject()

            this.sessionId = null
            this.projectId = null
            this.events = []
            this.streamingBaseEventIndex = 0
            this.isLoading = false
            this.isStreaming = false
            this.error = null
            this.streamCancelled = false
            this.draftMessage = ''
            // MYAC-919 — the attached object belonged to the old conversation.
            this.pendingFocus = null
            // MYAC-1182 — pending attachments were uploaded under the OLD session
            // id; they have no meaning (and no valid retry target) once it's gone.
            useChatAttachmentsStore().clearAttachments()
            // MYAC-796 — drop action state tied to the old conversation.
            this.resolvedProposalIds = []
            this.resultCards = []
            logger.debug('Session reset')

            if (opts.userInitiated) {
                try { trackAction(CsmAction.ChatSessionReset, { projectId: projectId || undefined }) } catch { }
            }
        },

        /**
         * MYAC-796 — respond to an action confirmation card. Hides the approval card and
         * sends a machine sentinel message the chatbot resolves deterministically (the LLM
         * is not involved). On approve, pushes a persistent result card that tracks the run.
         */
        async respondToActionProposal(
            proposal: ActionApprovalResponse,
            decision: 'approve' | 'decline',
        ): Promise<void> {
            const proposalId = proposal.proposal_id
            if (!this.resolvedProposalIds.includes(proposalId)) {
                this.resolvedProposalIds.push(proposalId)
            }
            const rawLocale = i18n.global.locale
            const locale = typeof rawLocale === 'string' ? rawLocale : rawLocale.value
            // Conversation language first (card stamp → store getter), app locale last —
            // the server-authored completion texts then match the conversation.
            const language = proposal.language === 'french' || proposal.language === 'english'
                ? proposal.language
                : (this.conversationLanguage ?? (locale?.startsWith('fr') ? 'french' : 'english'))
            if (this.projectMode && this.projectId && this.sessionId) {
                // MYAC-1519 — the decision is its own command, not a sentinel message.
                // The follow-ups run once the decision is DONE — after the timeout too,
                // if it gets there — and never on a refused or failed one.
                await this.dispatchDecision(this.projectId, this.sessionId, {
                    proposalId, decision, language,
                }, () => this.afterActionDecision(proposal, decision))
                return
            }
            const sentinel = `${ACTION_DECISION_PREFIX} ${JSON.stringify({
                proposal_id: proposalId,
                decision,
                language,
            })}`
            await this.sendMessage(sentinel)
            await this.afterActionDecision(proposal, decision)
        },

        /** What follows a decision on an action card, once the decision went through. */
        async afterActionDecision(
            proposal: ActionApprovalResponse,
            decision: 'approve' | 'decline',
        ): Promise<void> {
            const proposalId = proposal.proposal_id

            // The re-assessment is raised HERE, by the browser, not by the chatbot.
            //
            // It is a command doc, and the client data contract (§2) requires
            // `requested_by == auth.uid` — the shape the rules create-guard enforces. The
            // agent has no uid; it was writing `chat:<session>` with the Admin SDK, which
            // slipped past the rules only because backend writes bypass them, and left a
            // document nothing could tie back to a person.
            //
            // Raising it here also buys what the agent could never have: `sendAssessmentIntent`
            // resolves on the real ADMISSION verdict and keeps a listener for the outcome, so
            // a refusal is reported as a refusal, and completion can be announced instead of
            // promised. Ordering matters — the sentinel is awaited first, so the proof is on
            // the workitem before the run that must read it is requested.
            if (decision === 'approve'
                && (proposal.action === 'propose_requirement_repair'
                    || proposal.action === 'propose_reassessment')) {
                await this.requestRepairReassessment(proposal)
            }

            // Push the result card AFTER the approve turn resolves, so it anchors below the
            // agent's "Started…" reply (not above it). Persists in history from there.
            // Only the refresh spawns a run-tracking card — register edits (MYAC-848) are
            // near-instant and report completion via the chat notification instead.
            if (decision === 'approve' && proposal.action === 'trigger_register_refresh') {
                const register = proposal.register
                if (register && (ACTION_REGISTERS as string[]).includes(register)) {
                    this.resultCards.push({
                        id: proposalId,
                        register: register as ActionRegister,
                        startedAt: Date.now(),
                        afterMessageId: this.messages[this.messages.length - 1]?.id ?? null,
                    })
                }
            }
        },

        /**
         * MYAC-1519 — write a `decide_action` command and run `followUps` (the
         * re-assessment, the result card) once it is `done`, so they happen after
         * it, as they did after the sentinel's turn. Waits up to
         * `DECISION_SETTLE_MS`; past that it returns and keeps watching, and the
         * follow-ups run if and when the command reaches `done`. Refused or failed:
         * no follow-ups, the card is back with a toast (`onCommandUpdate`).
         * Resolves `done`, `refused` (refused / failed / cancelled / not written)
         * or `pending` (still running at the timeout).
         */
        async dispatchDecision(
            projectId: string,
            chatId: string,
            d: {
                proposalId: string
                decision: 'approve' | 'decline'
                language: 'french' | 'english'
                /** The register-proposal card: the backend rebuilds its exact decision. */
                action?: string
                register?: string
            },
            followUps?: () => Promise<void>,
        ): Promise<'done' | 'refused' | 'pending'> {
            this.error = null
            this.turnNotice = null
            const commandId = mintCommandId(projectId)
            this.pendingCommands.push({
                commandId,
                kind: 'decide',
                text: '',
                proposalId: d.proposalId,
                status: 'requested',
                baseSeq: this.nextRowSeq(),
                echoed: true,
                at: Date.now(),
            })
            const settled = this.waitForCommand(commandId, DECISION_SETTLE_MS)
            this.syncProjectRunState()
            try {
                await decideActionCommand(projectId, {
                    chat_id: chatId,
                    app_name: this.chatbotName ?? '',
                    proposal_id: d.proposalId,
                    decision: d.decision,
                    language: d.language,
                    ...(d.action ? { action: d.action } : {}),
                    ...(d.register ? { register: d.register } : {}),
                }, { commandId })
            } catch (error) {
                logger.error('chat: decide_action command could not be written', error)
                this.onCommandUpdate({ commandId, status: 'rejected', reasonKey: 'dispatch' })
                return 'refused'
            }
            this.followCommand(projectId, commandId)
            const status = await settled
            if (status === 'done') {
                await followUps?.()
                return 'done'
            }
            if (status !== 'timeout') return 'refused'
            // Still going: keep watching; the follow-ups wait for `done`.
            void this.waitForCommand(commandId, null).then(async (late) => {
                if (late !== 'done') return
                try {
                    await followUps?.()
                } catch (error) {
                    logger.error('chat: follow-ups of a late decision failed', error)
                }
            })
            return 'pending'
        },

        /**
         * Raise the scoped re-assessment behind an approved repair card, and tell the
         * conversation how it went.
         *
         * Admission is reported honestly: a refusal (no credits, that standard already
         * running) comes back as a refusal, because the agent cannot see the verdict and
         * used to claim success on the strength of an HTTP 200.
         *
         * Completion arrives minutes later through `onOutcome`, and is handed to the agent
         * as a hidden follow-up — the same machinery the register refresh uses. The agent
         * then re-reads the requirement and reports what actually changed, which is the
         * only way the chat can answer "did it move?" without polling.
         */
        async requestRepairReassessment(proposal: ActionApprovalResponse): Promise<void> {
            // MYAC-1044 — the card's OWN project wins over the session's. The chat can
            // now read a requirement across the QMS<->TD link, so a repair raised from a
            // Product window may belong to the company's assessment; the session project
            // would file it against the product instead.
            const projectId = proposal.project_id
                || this.projectId || useProjectsStore().currentProjectId
            const adoptionId = proposal.adoption_id
            // A scoped re-assessment carries its own uids and an EMPTY list is legitimate
            // (the whole referential); a repair re-judges the single requirement it
            // recorded input against.
            const isScoped = proposal.action === 'propose_reassessment'
            const uids = isScoped
                ? (proposal.requirement_uids ?? [])
                : (proposal.requirement_uid ? [proposal.requirement_uid] : [])
            const requirement = proposal.requirement
                ?? proposal.scope_label ?? proposal.requirement_uid ?? ''
            if (!projectId || !adoptionId || (!isScoped && !uids.length)) {
                logger.warn('reassessment skipped: missing project/adoption/uid', {
                    projectId, adoptionId, action: proposal.action,
                })
                return
            }
            try {
                const admission = await sendAssessmentIntent(
                    projectId,
                    'reassess',
                    { adoption_id: adoptionId, requirement_uids: uids },
                    {
                        onOutcome: (outcome) => {
                            // Hidden from the transcript; the agent's answer is what shows.
                            const instruction = outcome.status === 'done'
                                ? `The re-assessment of ${requirement} has finished. Read it again and tell me plainly what changed versus before: the rating, and what the assessor now says about the input I gave you.`
                                : `The re-assessment of ${requirement} failed. Tell me plainly, and that my input is still recorded against it.`
                            void this.sendMessage(
                                `${ACTION_FOLLOWUP_PREFIX} ${instruction}`,
                            )
                        },
                    },
                )
                if (admission.status !== 'accepted') {
                    const appStore = useAppStore()
                    appStore.toast(
                        i18n.global.t('chat.action_approval.repair_not_started'),
                        i18n.global.t(
                            // `internal` is the codebase's fallback (requirementsAssessment.ts uses the
                            // same): `unknown` has no key, so an admission refused without a
                            // reason_key would have leaked the raw key string into the toast.
                            `referentials.intents.reason.${admission.reasonKey || 'internal'}`,
                        ),
                        'warn',
                    )
                }
            } catch (e) {
                // Loud, not just logged. The agent has already told the user it asked for
                // the re-assessment, so swallowing this leaves them watching for a run that
                // was never raised — the same silent failure the agent's old "I'll tell you
                // when it's done" produced, moved one layer down. Their proof IS recorded;
                // say both things.
                logger.error('repair reassessment intent failed', e)
                useAppStore().toast(
                    i18n.global.t('referentials.intents.reason.dispatch'),
                    i18n.global.t('chat.action_approval.repair_run_failed', { requirement }),
                    'error',
                )
            }
        },

        /**
         * MYAC-883 — respond to the post-run delta card: approve the staged changes or
         * discard them. Sends the same machine sentinel as the other cards, with the
         * proposal's Firestore id — the chatbot resolves it against the proposal document,
         * not this session's state, so a run that finished in another session (or before a
         * reload) can still be answered here.
         *
         * Free: the extraction was paid for when it ran. Approving only writes the table
         * the user just reviewed.
         */
        async respondToRegisterProposal(
            proposalId: string,
            register: ActionRegister,
            decision: 'approve' | 'decline',
        ): Promise<void> {
            if (!this.resolvedProposalIds.includes(proposalId)) {
                this.resolvedProposalIds.push(proposalId)
            }
            const rawLocale = i18n.global.locale
            const locale = typeof rawLocale === 'string' ? rawLocale : rawLocale.value
            const language = this.conversationLanguage
                ?? (locale?.startsWith('fr') ? 'french' : 'english')
            if (this.projectMode && this.projectId && this.sessionId) {
                // MYAC-1519 — a decide_action command; the backend rebuilds the decision.
                await this.dispatchDecision(this.projectId, this.sessionId, {
                    proposalId, decision, language, action: APPLY_PROPOSAL_ACTION, register,
                })
                return
            }
            const sentinel = `${ACTION_DECISION_PREFIX} ${JSON.stringify({
                action: APPLY_PROPOSAL_ACTION,
                proposal_id: proposalId,
                register,
                decision,
                language,
            })}`
            await this.sendMessage(sentinel)
        },

        /**
         * Point the chat at a specific ADK app (e.g. the CAPA documentary agent),
         * overriding the global `chatbot_name`. Resets the session when the target
         * app changes so the new conversation is created against the right agent.
         * Pass `null` to fall back to the global chatbot.
         */
        async setAppName(appName: string | null): Promise<void> {
            if (this.appNameOverride === appName) {
                return
            }
            this.appNameOverride = appName
            await this.resetSession()
        },
    },
})

